import { useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  DirectorOnboarding,
  useDirectorOnboarding,
} from "@/components/DirectorOnboarding";

// Helper para convertir timestamp a Date de forma segura
const toSafeDate = (timestamp: unknown): Date => {
  if (!timestamp) return new Date();
  if (timestamp instanceof Date) return timestamp;
  if (
    typeof timestamp === "object" &&
    "toDate" in timestamp &&
    typeof (timestamp as { toDate: () => Date }).toDate === "function"
  ) {
    return (timestamp as { toDate: () => Date }).toDate();
  }
  if (typeof timestamp === "object" && "seconds" in timestamp) {
    return new Date((timestamp as { seconds: number }).seconds * 1000);
  }
  if (typeof timestamp === "string" || typeof timestamp === "number") {
    return new Date(timestamp);
  }
  return new Date();
};

import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Badge,
  SectionBadge,
} from "@/components/ui";
import {
  Users,
  Award,
  Calendar,
  DollarSign,
  TrendingUp,
  Building2,
  Loader2,
  ArrowRight,
  AlertCircle,
  CheckCircle,
  Clock,
  User,
  FileText,
  Settings,
  Shield,
  Sparkles,
  Copy,
  Check as CheckIcon,
  Share2,
  MapPin,
  Ticket,
  ExternalLink,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useAccessibleClubs } from "@/hooks/useDataScope";
import { useAuthStore } from "@/stores/authStore";
import { getActiveSections } from "@/lib/utils";
import { SECTION_GRADIENT_CLASSES } from "@/lib/constants/sections";
import { usePermissions } from "@/hooks/usePermissions";
import {
  useMembersByClub,
  useScopedMembers,
  useMembersByLinkedUser,
  usePerson,
} from "@/hooks/usePersons";
import { useActivePeriod } from "@/hooks/useCatalogs";
import {
  useUpcomingEvents,
  useEventsByStatus,
  usePersonRegistrations,
  useClubRegistrations,
  useClubEventPaymentsAll,
} from "@/hooks/useEvents";
import { useClub } from "@/hooks/useOrganization";
import PendingConsentsBanner from "@/components/consent/PendingConsentsBanner";
import WardsPendingConsents from "@/components/consent/WardsPendingConsents";
import MyWardsCard from "@/pages/profile/components/MyWardsCard";
import EnablePushBanner from "@/components/notifications/EnablePushBanner";
import {
  useChargesByClubAndPeriod,
  useFinancialSummary,
} from "@/hooks/useFinance";
import { useMeetingsByClubAndPeriod } from "@/hooks/useAttendance";
import { useAchievementsByClub } from "@/hooks/useProgress";
import { useAssociations } from "@/hooks/useOrganization";
import { useSystemUsers } from "@/hooks/useUsers";
import { useRoleAssignments } from "@/hooks/useRoles";
import { formatCurrency } from "@/lib/utils";
import type { ClubType } from "@/types";

// ============================================
// COMPONENTE: BANNER DE CÓDIGO DE INVITACIÓN
// ============================================
function InviteCodeBanner({ inviteCode }: { inviteCode: string }) {
  const [copied, setCopied] = useState(false);

  const inviteUrl = `${window.location.origin}/setup?invite=${inviteCode}`;

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(inviteUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [inviteUrl]);

  const handleWhatsApp = useCallback(() => {
    const text = encodeURIComponent(
      `¡Únete a nuestro club en Insignia! Haz clic en este enlace para registrarte:\n${inviteUrl}`,
    );
    window.open(`https://wa.me/?text=${text}`, "_blank");
  }, [inviteUrl]);

  return (
    <div className="relative overflow-hidden bg-gradient-to-r from-accent-50 to-primary-50 border border-accent-200 rounded-2xl p-5 shadow-sm">
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-gradient-to-br from-accent-400 to-accent-600 rounded-xl flex items-center justify-center shadow-md shadow-accent-500/25 shrink-0">
            <Share2 className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-600">
              Enlace de invitación de tu club
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              Comparte este enlace para que nuevos miembros se unan directamente
            </p>
          </div>
        </div>
        {/* URL visible */}
        <div className="flex items-center gap-2 bg-white border border-accent-200 rounded-xl px-4 py-2.5 min-w-0">
          <span className="text-xs text-gray-400 shrink-0">🔗</span>
          <span className="text-xs text-accent-700 font-mono truncate flex-1 select-all">
            {inviteUrl}
          </span>
          <code className="text-sm font-bold tracking-widest text-accent-600 font-mono shrink-0 pl-2 border-l border-accent-100">
            {inviteCode}
          </code>
        </div>
        {/* Botones */}
        <div className="flex gap-2">
          <button
            onClick={handleCopy}
            className="flex items-center gap-2 px-4 py-2.5 bg-accent-600 hover:bg-accent-700 text-white rounded-xl text-sm font-medium transition-colors shadow-md shadow-accent-500/25"
          >
            {copied ? (
              <CheckIcon className="w-4 h-4" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
            {copied ? "¡Copiado!" : "Copiar enlace"}
          </button>
          <button
            onClick={handleWhatsApp}
            className="flex items-center gap-2 px-4 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded-xl text-sm font-medium transition-colors shadow-md shadow-green-500/25"
          >
            <Share2 className="w-4 h-4" />
            Compartir
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// COMPONENTE: DASHBOARD PARA MIEMBRO
// ============================================
function MemberDashboard() {
  const { profile, user } = useAuthStore();
  const { data: upcomingEvents } = useUpcomingEvents();

  // Membresías (clubes a los que pertenece el usuario)
  const { data: myMemberships } = useMembersByLinkedUser(user?.uid);
  const primaryMembership = myMemberships?.[0];
  const { data: myClub } = useClub(primaryMembership?.clubId ?? "");

  // Datos personales (foto, documentos, completitud)
  const { data: person } = usePerson(profile?.personId);

  // Inscripciones del miembro (en curso y próximas)
  const { data: myRegistrations } = usePersonRegistrations(profile?.personId);

  const activeRegistrations = useMemo(
    () =>
      (myRegistrations ?? []).filter((r) =>
        ["pending", "approved", "waitlist"].includes(r.status),
      ),
    [myRegistrations],
  );

  // Próximo evento (más cercano de los inscritos)
  const nextRegistration = useMemo(() => {
    if (!activeRegistrations.length || !upcomingEvents) return null;
    const now = Date.now();
    const enriched = activeRegistrations
      .map((reg) => {
        const evt = upcomingEvents.find((e) => e.id === reg.eventId);
        if (!evt) return null;
        const start = toSafeDate(evt.startDate).getTime();
        return {
          reg,
          event: evt,
          daysLeft: Math.ceil((start - now) / 86400000),
        };
      })
      .filter(
        (
          x,
        ): x is {
          reg: NonNullable<typeof x>["reg"];
          event: NonNullable<typeof x>["event"];
          daysLeft: number;
        } => !!x && x.daysLeft >= 0,
      )
      .sort((a, b) => a.daysLeft - b.daysLeft);
    return enriched[0] ?? null;
  }, [activeRegistrations, upcomingEvents]);

  // Eventos disponibles (publicados aún no inscritos)
  const availableEvents = useMemo(() => {
    if (!upcomingEvents) return [];
    const registeredIds = new Set(activeRegistrations.map((r) => r.eventId));
    return upcomingEvents.filter((e) => !registeredIds.has(e.id));
  }, [upcomingEvents, activeRegistrations]);

  // Nota: el pago de eventos es por club, no por persona. La UI del miembro
  // ya no muestra cifras "por pagar" individuales — eso vive en el panel del director.

  // Completitud del perfil (foto, documentos, EPS, contacto emergencia)
  const profileCompletion = useMemo(() => {
    if (!person) return { pct: 0, missing: [] as string[] };
    const checks: Array<{ ok: boolean; label: string }> = [
      { ok: !!person.photoUrl, label: "Foto de perfil" },
      { ok: !!person.documentFrontUrl, label: "Documento (anverso)" },
      { ok: !!person.documentBackUrl, label: "Documento (reverso)" },
      { ok: !!person.epsDocumentUrl, label: "Soporte EPS" },
      { ok: !!person.healthInsurance, label: "EPS / seguro de salud" },
      {
        ok: !!person.emergencyContact?.phone,
        label: "Contacto de emergencia",
      },
      { ok: !!person.address, label: "Dirección" },
      { ok: !!person.bloodType, label: "Tipo de sangre" },
    ];
    const done = checks.filter((c) => c.ok).length;
    return {
      pct: Math.round((done / checks.length) * 100),
      missing: checks.filter((c) => !c.ok).map((c) => c.label),
    };
  }, [person]);

  const initials = (profile?.displayName || user?.email || "??")
    .split(/\s+/)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const formatDate = (d: Date) =>
    d.toLocaleDateString("es-CO", {
      weekday: "short",
      day: "numeric",
      month: "short",
    });

  return (
    <div className="space-y-6">
      {/* Invitación a activar push notifications (auto-oculta si ya están activas o no soportadas) */}
      <EnablePushBanner />

      {/* Banner de consentimientos pendientes (no bloqueante) */}
      <PendingConsentsBanner variant="card" />

      {/* Check-ins pendientes de las personas a cargo (acudiente) */}
      <WardsPendingConsents />

      {/* Personas a mi cargo (acudiente): visible solo si tiene dependientes */}
      <MyWardsCard hideWhenEmpty />

      {/* HERO: bienvenida + métricas rápidas */}
      <div className="relative overflow-hidden bg-gradient-to-br from-primary-600 via-primary-500 to-accent-500 rounded-2xl p-6 sm:p-8 text-white shadow-xl shadow-primary-500/25">
        <div className="absolute inset-0 bg-mesh opacity-30" />
        <div className="relative grid grid-cols-1 lg:grid-cols-[auto_1fr_auto] gap-6 items-center">
          {/* Avatar */}
          <div className="flex items-center gap-4">
            {person?.photoUrl ? (
              <img
                src={person.photoUrl}
                alt={profile?.displayName ?? ""}
                className="w-20 h-20 rounded-2xl object-cover ring-4 ring-white/30 shadow-lg"
              />
            ) : (
              <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center ring-4 ring-white/30 text-2xl font-bold">
                {initials}
              </div>
            )}
            <div>
              <p className="text-primary-100 text-xs font-medium">
                Hola{person?.firstName ? "," : " de vuelta"}
              </p>
              <h1 className="text-2xl sm:text-3xl font-bold leading-tight">
                {person?.firstName || profile?.displayName || user?.email}
              </h1>
              {primaryMembership?.section && myClub && (
                <p className="text-primary-100 text-sm mt-1">
                  {myClub.name} ·{" "}
                  <span className="capitalize">
                    {primaryMembership.section.replace("_", " ")}
                  </span>
                </p>
              )}
            </div>
          </div>

          {/* KPIs */}
          <div className="grid grid-cols-3 gap-3 lg:max-w-md lg:mx-auto w-full">
            <HeroKpi
              icon={<Ticket className="w-4 h-4" />}
              value={activeRegistrations.length.toString()}
              label="Inscripciones"
            />
            <HeroKpi
              icon={<Clock className="w-4 h-4" />}
              value={
                nextRegistration
                  ? nextRegistration.daysLeft === 0
                    ? "Hoy"
                    : `${nextRegistration.daysLeft}d`
                  : "—"
              }
              label="Próximo evento"
            />
            <HeroKpi
              icon={<CheckCircle className="w-4 h-4" />}
              value={`${profileCompletion.pct}%`}
              label="Perfil completo"
            />
          </div>

          {/* CTA principal */}
          <div className="flex flex-col gap-2 lg:items-end">
            {nextRegistration ? (
              <Link
                to={`/events/${nextRegistration.event.id}`}
                className="inline-flex items-center gap-2 bg-white text-primary-700 hover:bg-primary-50 px-4 py-2.5 rounded-xl font-semibold shadow-lg transition-all hover:shadow-xl"
              >
                Ver mi próximo evento
                <ArrowRight className="w-4 h-4" />
              </Link>
            ) : (
              <Link
                to="/events"
                className="inline-flex items-center gap-2 bg-white text-primary-700 hover:bg-primary-50 px-4 py-2.5 rounded-xl font-semibold shadow-lg transition-all hover:shadow-xl"
              >
                Explorar eventos
                <ArrowRight className="w-4 h-4" />
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Próximo evento destacado */}
      {nextRegistration && (
        <Card className="border-0 shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-purple-600 to-pink-500 px-5 py-2 flex items-center justify-between">
            <span className="text-white text-xs font-semibold uppercase tracking-wide flex items-center gap-1">
              <Sparkles className="w-3.5 h-3.5" />
              Tu próxima aventura
            </span>
            <CountdownPill daysLeft={nextRegistration.daysLeft} />
          </div>
          <CardContent className="py-5">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 text-white flex flex-col items-center justify-center shadow-md shrink-0">
                <p className="text-2xl font-bold leading-none">
                  {toSafeDate(nextRegistration.event.startDate).getDate()}
                </p>
                <p className="text-[10px] uppercase font-semibold mt-1">
                  {toSafeDate(
                    nextRegistration.event.startDate,
                  ).toLocaleDateString("es-CO", { month: "short" })}
                </p>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-lg text-gray-900 truncate">
                  {nextRegistration.event.name}
                </h3>
                <div className="flex items-center gap-3 text-sm text-gray-600 mt-1 flex-wrap">
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5 text-gray-400" />
                    {nextRegistration.event.location?.city}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5 text-gray-400" />
                    {formatDate(toSafeDate(nextRegistration.event.startDate))}
                  </span>
                  <Badge
                    className={
                      nextRegistration.reg.status === "approved"
                        ? "bg-green-100 text-green-700 border-0"
                        : nextRegistration.reg.status === "waitlist"
                          ? "bg-blue-100 text-blue-700 border-0"
                          : "bg-amber-100 text-amber-700 border-0"
                    }
                  >
                    {nextRegistration.reg.status === "approved"
                      ? "Aprobado"
                      : nextRegistration.reg.status === "waitlist"
                        ? "Lista de espera"
                        : "Pendiente"}
                  </Badge>
                </div>
              </div>
              <div className="flex flex-col sm:items-end gap-1 shrink-0">
                <Link
                  to={`/events/${nextRegistration.event.id}`}
                  className="inline-flex items-center gap-1 text-purple-700 hover:text-purple-900 text-sm font-medium"
                >
                  Ver detalle <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Grid principal: perfil + club */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Completitud del perfil */}
        <Card className="border-0 shadow-lg lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <div className="w-7 h-7 bg-gradient-to-br from-blue-400 to-blue-600 rounded-lg flex items-center justify-center">
                <User className="w-3.5 h-3.5 text-white" />
              </div>
              Mi perfil
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-gray-600">Completitud</span>
                <span className="font-semibold text-gray-900">
                  {profileCompletion.pct}%
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all ${
                    profileCompletion.pct >= 80
                      ? "bg-green-500"
                      : profileCompletion.pct >= 50
                        ? "bg-amber-500"
                        : "bg-red-400"
                  }`}
                  style={{ width: `${profileCompletion.pct}%` }}
                />
              </div>
            </div>

            {profileCompletion.missing.length > 0 ? (
              <div>
                <p className="text-xs font-semibold text-gray-600 mb-2">
                  Pendiente:
                </p>
                <ul className="space-y-1.5">
                  {profileCompletion.missing.slice(0, 4).map((m) => (
                    <li
                      key={m}
                      className="text-xs text-gray-700 flex items-center gap-1.5"
                    >
                      <AlertCircle className="w-3 h-3 text-amber-500 shrink-0" />
                      {m}
                    </li>
                  ))}
                  {profileCompletion.missing.length > 4 && (
                    <li className="text-[11px] text-gray-500">
                      +{profileCompletion.missing.length - 4} más
                    </li>
                  )}
                </ul>
              </div>
            ) : (
              <p className="text-xs text-emerald-600 flex items-center gap-1.5">
                <CheckCircle className="w-3.5 h-3.5" />
                Tu perfil está completo. ¡Listo para inscribirte!
              </p>
            )}

            <Link
              to="/profile"
              className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              Completar mi perfil <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </CardContent>
        </Card>

        {/* Mi club */}
        {primaryMembership && myClub ? (
          <Card className="border-0 shadow-lg lg:col-span-2 overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-500 to-teal-500 px-5 py-3 flex items-center gap-3">
              {myClub.logoUrl ? (
                <img
                  src={myClub.logoUrl}
                  alt={myClub.name}
                  className="w-10 h-10 rounded-xl object-cover ring-2 ring-white/40"
                />
              ) : (
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-emerald-50 text-[11px] font-medium uppercase tracking-wide">
                  Tu club
                </p>
                <p className="text-white font-bold text-lg truncate">
                  {myClub.name}
                </p>
              </div>
              <Badge
                className={
                  primaryMembership.status === "active"
                    ? "bg-white/20 text-white border-0"
                    : "bg-amber-200 text-amber-900 border-0"
                }
              >
                {primaryMembership.status === "active"
                  ? "Activo"
                  : primaryMembership.status}
              </Badge>
            </div>
            <CardContent className="py-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                {primaryMembership.section && (
                  <InfoRow
                    icon={<Award className="w-4 h-4 text-emerald-600" />}
                    label="Sección"
                    value={
                      <span className="capitalize">
                        {primaryMembership.section.replace("_", " ")}
                      </span>
                    }
                  />
                )}
                {myClub.directorName && (
                  <InfoRow
                    icon={<User className="w-4 h-4 text-emerald-600" />}
                    label="Director"
                    value={myClub.directorName}
                  />
                )}
                {myClub.churchName && (
                  <InfoRow
                    icon={<Building2 className="w-4 h-4 text-emerald-600" />}
                    label="Iglesia"
                    value={myClub.churchName}
                  />
                )}
                {myClub.meetingDay && myClub.meetingTime && (
                  <InfoRow
                    icon={<Calendar className="w-4 h-4 text-emerald-600" />}
                    label="Reunión"
                    value={`${myClub.meetingDay} ${myClub.meetingTime}`}
                  />
                )}
                {myClub.contactPhone && (
                  <InfoRow
                    icon={<Sparkles className="w-4 h-4 text-emerald-600" />}
                    label="Contacto"
                    value={myClub.contactPhone}
                  />
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-0 shadow-lg lg:col-span-2 bg-gradient-to-br from-amber-50 to-orange-50">
            <CardContent className="py-8 text-center">
              <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
                <Building2 className="w-7 h-7 text-amber-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-1">
                Aún no perteneces a un club
              </h3>
              <p className="text-sm text-gray-600 max-w-md mx-auto">
                Pide a tu director el código de invitación para vincularte y
                participar en eventos.
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Mis inscripciones activas (lista) */}
      {activeRegistrations.length > 0 && (
        <Card className="border-0 shadow-lg">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <div className="w-7 h-7 bg-gradient-to-br from-purple-400 to-purple-600 rounded-lg flex items-center justify-center">
                <Ticket className="w-3.5 h-3.5 text-white" />
              </div>
              Mis inscripciones ({activeRegistrations.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {activeRegistrations.slice(0, 5).map((reg) => (
                <Link
                  key={reg.id}
                  to={`/events/${reg.eventId}`}
                  className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:border-purple-200 hover:bg-purple-50/30 transition-colors group"
                >
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-purple-100 to-purple-200 text-purple-700 flex items-center justify-center shrink-0">
                    <Calendar className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-gray-900 truncate group-hover:text-purple-700">
                      {reg.eventName}
                    </p>
                    <p className="text-xs text-gray-500">
                      {reg.category} · Inscrito
                    </p>
                  </div>
                  <Badge
                    className={
                      reg.status === "approved"
                        ? "bg-green-100 text-green-700 border-0"
                        : reg.status === "waitlist"
                          ? "bg-blue-100 text-blue-700 border-0"
                          : "bg-amber-100 text-amber-700 border-0"
                    }
                  >
                    {reg.status === "approved"
                      ? "Aprobado"
                      : reg.status === "waitlist"
                        ? "Espera"
                        : "Pendiente"}
                  </Badge>
                  <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-purple-500 group-hover:translate-x-0.5 transition-transform" />
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Eventos disponibles para inscribirse */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <div className="w-7 h-7 bg-gradient-to-br from-pink-400 to-rose-500 rounded-lg flex items-center justify-center">
              <Sparkles className="w-3.5 h-3.5 text-white" />
            </div>
            Eventos disponibles
          </CardTitle>
        </CardHeader>
        <CardContent>
          {availableEvents.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {availableEvents.slice(0, 4).map((event) => (
                <Link
                  key={event.id}
                  to={`/events/${event.id}`}
                  className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:border-pink-200 hover:bg-pink-50/30 transition-colors group"
                >
                  <div className="flex-shrink-0 w-12 h-12 bg-white rounded-xl shadow-sm flex flex-col items-center justify-center border border-gray-100">
                    <p className="text-base font-bold text-gray-900 leading-none">
                      {toSafeDate(event.startDate).getDate()}
                    </p>
                    <p className="text-[9px] text-gray-500 uppercase font-medium mt-0.5">
                      {toSafeDate(event.startDate).toLocaleDateString("es-CO", {
                        month: "short",
                      })}
                    </p>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-gray-900 truncate group-hover:text-pink-700">
                      {event.name}
                    </p>
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {event.location?.city}
                    </p>
                  </div>
                  <ExternalLink className="w-3.5 h-3.5 text-gray-300 group-hover:text-pink-500" />
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 text-sm">
              <Calendar className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p>No hay eventos disponibles por ahora.</p>
              <p className="text-xs mt-1">
                Te avisaremos cuando se publiquen nuevos.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Mensaje informativo */}
      <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 border-0 shadow-md">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
              <Shield className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="font-semibold text-blue-900 text-sm mb-0.5">
                ¿Necesitas más acceso?
              </p>
              <p className="text-xs text-blue-700">
                Si eres director de un club o necesitas funciones adicionales,
                contacta a tu administrador de asociación.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ----- helpers de MemberDashboard -----
function HeroKpi({
  icon,
  value,
  label,
}: {
  icon: React.ReactNode;
  value: string;
  label: string;
}) {
  return (
    <div className="bg-white/15 backdrop-blur-sm rounded-xl px-3 py-2 ring-1 ring-white/20">
      <div className="flex items-center gap-1.5 text-primary-100 text-[11px] font-medium">
        {icon}
        {label}
      </div>
      <p className="text-xl font-bold leading-tight mt-0.5">{value}</p>
    </div>
  );
}

function CountdownPill({ daysLeft }: { daysLeft: number }) {
  const text =
    daysLeft === 0
      ? "¡Es hoy!"
      : daysLeft === 1
        ? "Mañana"
        : daysLeft <= 7
          ? `En ${daysLeft} días`
          : `En ${daysLeft} días`;
  return (
    <span className="bg-white/20 text-white text-[11px] font-semibold px-2.5 py-0.5 rounded-full backdrop-blur-sm">
      {text}
    </span>
  );
}

function InfoRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-2">
      {icon}
      <span className="text-gray-500 text-xs">{label}:</span>
      <span className="font-medium text-gray-900 truncate">{value}</span>
    </div>
  );
}

// ============================================
// COMPONENTE: DASHBOARD PARA UNIÓN
// ============================================
function UnionDashboard() {
  const [selectedAssociationId, setSelectedAssociationId] =
    useState<string>("all");

  const { data: associations } = useAssociations();
  const { data: allClubs } = useAccessibleClubs();
  const { data: users } = useSystemUsers();
  const { data: roleAssignments } = useRoleAssignments();

  // Asociación seleccionada
  const selectedAssociation = useMemo(() => {
    if (selectedAssociationId === "all") return null;
    return associations?.find((a) => a.id === selectedAssociationId) || null;
  }, [associations, selectedAssociationId]);

  // Estadísticas generales
  const generalStats = useMemo(() => {
    const totalAssociations = associations?.length || 0;
    const totalClubs = allClubs?.length || 0;
    const activeUsers = users?.filter((u) => u.status === "active").length || 0;
    const pendingUsers =
      users?.filter((u) => u.status === "pending").length || 0;
    const associationAdmins =
      roleAssignments?.filter(
        (r) => r.role === "admin_association" && r.status === "active",
      ).length || 0;
    const clubDirectors =
      roleAssignments?.filter(
        (r) => r.role === "club_director" && r.status === "active",
      ).length || 0;

    return {
      totalAssociations,
      totalClubs,
      activeUsers,
      pendingUsers,
      associationAdmins,
      clubDirectors,
    };
  }, [associations, allClubs, users, roleAssignments]);

  // Estadísticas de asociación seleccionada
  const associationStats = useMemo(() => {
    if (selectedAssociationId === "all") return null;

    const clubsInAssoc =
      allClubs?.filter((c) => c.associationId === selectedAssociationId) || [];
    const clubIds = clubsInAssoc.map((c) => c.id);

    // Roles en esta asociación
    const adminsInAssoc =
      roleAssignments?.filter(
        (r) =>
          r.role === "admin_association" &&
          r.scopeId === selectedAssociationId &&
          r.status === "active",
      ).length || 0;

    const directorsInAssoc =
      roleAssignments?.filter(
        (r) =>
          r.role === "club_director" &&
          clubIds.includes(r.scopeId || "") &&
          r.status === "active",
      ).length || 0;

    // Clubes sin director
    const clubsWithoutDirector = clubsInAssoc.filter((club) => {
      const hasDirector = roleAssignments?.some(
        (r) =>
          r.role === "club_director" &&
          r.scopeId === club.id &&
          r.status === "active",
      );
      return !hasDirector;
    });

    return {
      totalClubs: clubsInAssoc.length,
      admins: adminsInAssoc,
      directors: directorsInAssoc,
      clubsWithoutDirector: clubsWithoutDirector.length,
      clubs: clubsInAssoc,
    };
  }, [selectedAssociationId, allClubs, roleAssignments]);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header con gradiente */}
      <div className="relative overflow-hidden bg-gradient-to-br from-purple-600 via-purple-500 to-fuchsia-500 rounded-2xl p-6 text-white shadow-xl shadow-purple-500/25">
        <div className="absolute inset-0 bg-mesh opacity-30" />
        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center ring-4 ring-white/30">
              <Shield className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Panel de la Unión</h1>
              <p className="text-purple-100 mt-0.5">
                Vista general de la organización
              </p>
            </div>
          </div>

          {/* Filtro de Asociación */}
          <div className="flex items-center gap-2">
            <label className="text-sm text-purple-100">Ver:</label>
            <select
              value={selectedAssociationId}
              onChange={(e) => setSelectedAssociationId(e.target.value)}
              className="px-4 py-2.5 border-2 border-white/20 rounded-xl bg-white/10 backdrop-blur-sm text-white focus:ring-4 focus:ring-white/20 focus:border-white/40 text-sm font-medium"
            >
              <option value="all" className="text-gray-900">
                Todas las asociaciones
              </option>
              {associations?.map((assoc) => (
                <option
                  key={assoc.id}
                  value={assoc.id}
                  className="text-gray-900"
                >
                  {assoc.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Vista General (cuando no hay filtro) */}
      {selectedAssociationId === "all" && (
        <>
          {/* Stats Grid - Modernos con hover */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            <Card className="border-0 shadow-lg group hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <CardContent className="py-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Asociaciones
                    </p>
                    <p className="text-3xl font-bold text-gray-900 mt-1">
                      {generalStats.totalAssociations}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">En el sistema</p>
                  </div>
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-400 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/30 group-hover:scale-110 transition-transform duration-300">
                    <Building2 className="w-7 h-7 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg group hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <CardContent className="py-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Clubes</p>
                    <p className="text-3xl font-bold text-gray-900 mt-1">
                      {generalStats.totalClubs}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Total registrados
                    </p>
                  </div>
                  <div className="w-14 h-14 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-500/30 group-hover:scale-110 transition-transform duration-300">
                    <Users className="w-7 h-7 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg group hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <CardContent className="py-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Usuarios Activos
                    </p>
                    <p className="text-3xl font-bold text-gray-900 mt-1">
                      {generalStats.activeUsers}
                    </p>
                    {generalStats.pendingUsers > 0 && (
                      <p className="text-xs text-orange-500 mt-1 font-medium">
                        {generalStats.pendingUsers} pendientes
                      </p>
                    )}
                  </div>
                  <div className="w-14 h-14 bg-gradient-to-br from-violet-400 to-violet-600 rounded-2xl flex items-center justify-center shadow-lg shadow-violet-500/30 group-hover:scale-110 transition-transform duration-300">
                    <User className="w-7 h-7 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg group hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <CardContent className="py-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Roles Asignados
                    </p>
                    <p className="text-3xl font-bold text-gray-900 mt-1">
                      {generalStats.associationAdmins +
                        generalStats.clubDirectors}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {generalStats.associationAdmins} admins ·{" "}
                      {generalStats.clubDirectors} directores
                    </p>
                  </div>
                  <div className="w-14 h-14 bg-gradient-to-br from-purple-400 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg shadow-purple-500/30 group-hover:scale-110 transition-transform duration-300">
                    <Shield className="w-7 h-7 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Usuarios pendientes - Alert Moderno */}
          {generalStats.pendingUsers > 0 && (
            <Card className="border-2 border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50 shadow-md">
              <CardContent className="py-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-amber-500 rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/25">
                      <AlertCircle className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-orange-800">
                        {generalStats.pendingUsers} usuario(s) pendiente(s) de
                        activación
                      </p>
                      <p className="text-sm text-orange-600">
                        Revisa y activa las cuentas nuevas
                      </p>
                    </div>
                  </div>
                  <Link
                    to="/users"
                    className="inline-flex items-center gap-1 px-4 py-2 bg-orange-100 hover:bg-orange-200 text-orange-700 font-medium text-sm rounded-xl transition-colors duration-200"
                  >
                    Ver usuarios
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Lista de Asociaciones con estadísticas - Moderno */}
          <Card className="border-0 shadow-lg">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center shadow-md shadow-blue-500/25">
                    <Building2 className="w-5 h-5 text-white" />
                  </div>
                  <span>Asociaciones</span>
                </div>
                <Link
                  to="/organization"
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 group transition-colors duration-200"
                >
                  Gestionar
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </Link>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {associations && associations.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b-2 border-gray-100">
                        <th className="text-left py-4 px-4 text-sm font-semibold text-gray-600">
                          Asociación
                        </th>
                        <th className="text-center py-4 px-4 text-sm font-semibold text-gray-600">
                          Clubes
                        </th>
                        <th className="text-center py-4 px-4 text-sm font-semibold text-gray-600">
                          Admin
                        </th>
                        <th className="text-center py-4 px-4 text-sm font-semibold text-gray-600">
                          Directores
                        </th>
                        <th className="text-right py-4 px-4 text-sm font-semibold text-gray-600">
                          Acción
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {associations.map((assoc) => {
                        const clubsCount =
                          allClubs?.filter((c) => c.associationId === assoc.id)
                            .length || 0;
                        const hasAdmin = roleAssignments?.some(
                          (r) =>
                            r.role === "admin_association" &&
                            r.scopeId === assoc.id &&
                            r.status === "active",
                        );
                        const directorsCount =
                          roleAssignments?.filter((r) => {
                            const clubIds =
                              allClubs
                                ?.filter((c) => c.associationId === assoc.id)
                                .map((c) => c.id) || [];
                            return (
                              r.role === "club_director" &&
                              clubIds.includes(r.scopeId || "") &&
                              r.status === "active"
                            );
                          }).length || 0;

                        return (
                          <tr
                            key={assoc.id}
                            className="border-b border-gray-50 hover:bg-gradient-to-r hover:from-gray-50 hover:to-transparent transition-colors duration-200"
                          >
                            <td className="py-4 px-4">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center shadow-md shadow-blue-500/20">
                                  <Building2 className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                  <p className="font-semibold text-gray-900">
                                    {assoc.name}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {assoc.region || "Sin región"}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className="py-4 px-4 text-center">
                              <Badge variant="success" size="sm">
                                {clubsCount}
                              </Badge>
                            </td>
                            <td className="py-4 px-4 text-center">
                              {hasAdmin ? (
                                <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center mx-auto shadow-sm shadow-emerald-500/25">
                                  <CheckCircle className="w-5 h-5 text-white" />
                                </div>
                              ) : (
                                <div className="w-8 h-8 bg-gradient-to-br from-orange-400 to-orange-600 rounded-xl flex items-center justify-center mx-auto shadow-sm shadow-orange-500/25">
                                  <AlertCircle className="w-5 h-5 text-white" />
                                </div>
                              )}
                            </td>
                            <td className="py-4 px-4 text-center">
                              <span className="font-semibold text-gray-700">
                                {directorsCount}
                              </span>
                            </td>
                            <td className="py-4 px-4 text-right">
                              <button
                                onClick={() =>
                                  setSelectedAssociationId(assoc.id)
                                }
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-primary-600 hover:text-primary-700 hover:bg-primary-50 font-medium rounded-xl transition-colors duration-200"
                              >
                                Ver detalle
                                <ArrowRight className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <div className="w-16 h-16 bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <Building2 className="w-8 h-8 text-gray-500" />
                  </div>
                  <p className="font-medium">No hay asociaciones registradas</p>
                  <Link
                    to="/organization"
                    className="inline-flex items-center gap-1 mt-3 px-4 py-2 bg-primary-50 hover:bg-primary-100 text-primary-600 font-medium rounded-xl transition-colors duration-200"
                  >
                    Crear asociación
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Vista de Asociación Específica */}
      {selectedAssociationId !== "all" &&
        selectedAssociation &&
        associationStats && (
          <>
            {/* Header de Asociación - Moderno */}
            <Card className="border-2 border-blue-200 bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 shadow-md">
              <CardContent className="py-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/25">
                      <Building2 className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">
                        {selectedAssociation.name}
                      </h2>
                      <p className="text-gray-600">
                        {selectedAssociation.region || "Sin región definida"}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedAssociationId("all")}
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-white hover:bg-gray-50 text-gray-700 font-medium text-sm rounded-xl border border-gray-200 shadow-sm transition-colors duration-200"
                  >
                    ← Volver a vista general
                  </button>
                </div>
              </CardContent>
            </Card>

            {/* Stats de Asociación - Modernos */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
              <Card className="border-0 shadow-lg group hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <CardContent className="py-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        Clubes
                      </p>
                      <p className="text-3xl font-bold text-gray-900 mt-1">
                        {associationStats.totalClubs}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/25 group-hover:scale-110 transition-transform duration-300">
                      <Users className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg group hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <CardContent className="py-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        Administradores
                      </p>
                      <p className="text-3xl font-bold text-gray-900 mt-1">
                        {associationStats.admins}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/25 group-hover:scale-110 transition-transform duration-300">
                      <Shield className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg group hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <CardContent className="py-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        Directores
                      </p>
                      <p className="text-3xl font-bold text-gray-900 mt-1">
                        {associationStats.directors}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-400 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/25 group-hover:scale-110 transition-transform duration-300">
                      <User className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card
                className={`border-0 shadow-lg group hover:shadow-xl hover:-translate-y-1 transition-all duration-300 ${
                  associationStats.clubsWithoutDirector > 0
                    ? "ring-2 ring-orange-200"
                    : ""
                }`}
              >
                <CardContent className="py-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        Sin Director
                      </p>
                      <p
                        className={`text-3xl font-bold mt-1 ${associationStats.clubsWithoutDirector > 0 ? "text-orange-600" : "text-emerald-600"}`}
                      >
                        {associationStats.clubsWithoutDirector}
                      </p>
                    </div>
                    <div
                      className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300 ${
                        associationStats.clubsWithoutDirector > 0
                          ? "bg-gradient-to-br from-orange-400 to-orange-600 shadow-orange-500/25"
                          : "bg-gradient-to-br from-emerald-400 to-emerald-600 shadow-emerald-500/25"
                      }`}
                    >
                      {associationStats.clubsWithoutDirector > 0 ? (
                        <AlertCircle className="w-6 h-6 text-white" />
                      ) : (
                        <CheckCircle className="w-6 h-6 text-white" />
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Alerta si no hay admin - Moderno */}
            {associationStats.admins === 0 && (
              <Card className="border-2 border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50 shadow-md">
                <CardContent className="py-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-amber-500 rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/25">
                        <AlertCircle className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-orange-800">
                          Esta asociación no tiene administrador asignado
                        </p>
                        <p className="text-sm text-orange-600">
                          Asigna un administrador para que pueda gestionar los
                          clubes
                        </p>
                      </div>
                    </div>
                    <Link
                      to="/roles"
                      className="inline-flex items-center gap-1 px-4 py-2 bg-orange-100 hover:bg-orange-200 text-orange-700 font-medium text-sm rounded-xl transition-colors duration-200"
                    >
                      Asignar admin
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Lista de Clubes de la Asociación - Moderno */}
            <Card className="border-0 shadow-lg">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center shadow-md shadow-emerald-500/25">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <CardTitle className="text-lg">
                    Clubes de {selectedAssociation.name}
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                {associationStats.clubs.length > 0 ? (
                  <div className="space-y-3">
                    {associationStats.clubs.map((club) => {
                      const hasDirector = roleAssignments?.some(
                        (r) =>
                          r.role === "club_director" &&
                          r.scopeId === club.id &&
                          r.status === "active",
                      );
                      const director = roleAssignments?.find(
                        (r) =>
                          r.role === "club_director" &&
                          r.scopeId === club.id &&
                          r.status === "active",
                      );
                      const directorUser = director
                        ? users?.find((u) => u.id === director.userId)
                        : null;

                      return (
                        <div
                          key={club.id}
                          className={`flex items-center justify-between p-4 rounded-xl border transition-all duration-300 hover:shadow-md ${
                            hasDirector
                              ? "bg-gradient-to-r from-emerald-50 to-white border-emerald-200 hover:border-emerald-300"
                              : "bg-gradient-to-r from-orange-50 to-white border-orange-200 hover:border-orange-300"
                          }`}
                        >
                          <div className="flex items-center gap-4">
                            <div
                              className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-md ${
                                hasDirector
                                  ? "bg-gradient-to-br from-emerald-400 to-emerald-600 shadow-emerald-500/25"
                                  : "bg-gradient-to-br from-orange-400 to-orange-600 shadow-orange-500/25"
                              }`}
                            >
                              <Users className="w-6 h-6 text-white" />
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">
                                {club.name}
                              </p>
                              <p className="text-sm text-gray-500">
                                {hasDirector && directorUser
                                  ? `Director: ${directorUser.displayName}`
                                  : "Sin director asignado"}
                              </p>
                            </div>
                          </div>
                          {!hasDirector && (
                            <Link
                              to="/roles"
                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary-50 hover:bg-primary-100 text-primary-600 font-medium text-sm rounded-xl transition-colors duration-200"
                            >
                              Asignar director
                              <ArrowRight className="w-4 h-4" />
                            </Link>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <div className="w-16 h-16 bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <Users className="w-8 h-8 text-gray-500" />
                    </div>
                    <p className="font-medium">
                      No hay clubes registrados en esta asociación
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

      {/* Acciones Rápidas - Modernas */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-primary-400 to-primary-600 rounded-xl flex items-center justify-center shadow-md shadow-primary-500/25">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <CardTitle className="text-lg">Acciones Rápidas</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Link to="/organization" className="group">
              <div className="flex flex-col items-center gap-3 p-5 bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border border-slate-200">
                <div className="w-14 h-14 bg-gradient-to-br from-slate-500 to-slate-700 rounded-xl flex items-center justify-center shadow-lg shadow-slate-500/25 group-hover:scale-110 transition-transform duration-300">
                  <Building2 className="w-7 h-7 text-white" />
                </div>
                <span className="text-sm font-semibold text-gray-700">
                  Asociaciones
                </span>
              </div>
            </Link>
            <Link to="/users" className="group">
              <div className="flex flex-col items-center gap-3 p-5 bg-gradient-to-br from-violet-50 to-violet-100 rounded-2xl hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border border-violet-200">
                <div className="w-14 h-14 bg-gradient-to-br from-violet-500 to-violet-700 rounded-xl flex items-center justify-center shadow-lg shadow-violet-500/25 group-hover:scale-110 transition-transform duration-300">
                  <User className="w-7 h-7 text-white" />
                </div>
                <span className="text-sm font-semibold text-gray-700">
                  Usuarios
                </span>
              </div>
            </Link>
            <Link to="/roles" className="group">
              <div className="flex flex-col items-center gap-3 p-5 bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border border-purple-200">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-700 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/25 group-hover:scale-110 transition-transform duration-300">
                  <Shield className="w-7 h-7 text-white" />
                </div>
                <span className="text-sm font-semibold text-gray-700">
                  Asignar Roles
                </span>
              </div>
            </Link>
            <Link to="/reports" className="group">
              <div className="flex flex-col items-center gap-3 p-5 bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-2xl hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border border-cyan-200">
                <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-cyan-700 rounded-xl flex items-center justify-center shadow-lg shadow-cyan-500/25 group-hover:scale-110 transition-transform duration-300">
                  <FileText className="w-7 h-7 text-white" />
                </div>
                <span className="text-sm font-semibold text-gray-700">
                  Reportes
                </span>
              </div>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================
// COMPONENTE: DASHBOARD PARA ASOCIACIÓN
// ============================================
function AssociationDashboard() {
  const { profile } = useAuthStore();
  const { data: allClubs } = useAccessibleClubs();
  const { data: roleAssignments } = useRoleAssignments();
  const { data: associations } = useAssociations();
  const { data: members } = useScopedMembers();

  // Obtener la asociación del admin
  const myAssociationId = profile?.scopeId;
  const myAssociation = associations?.find((a) => a.id === myAssociationId);

  // Filtrar clubes de mi asociación
  const myClubs = useMemo(() => {
    if (!allClubs || !myAssociationId) return [];
    return allClubs.filter((c) => c.associationId === myAssociationId);
  }, [allClubs, myAssociationId]);

  // Miembros de mi asociación
  const myMembers = useMemo(() => {
    if (!members || !myAssociationId) return [];
    return members.filter((m) => m.associationId === myAssociationId);
  }, [members, myAssociationId]);

  // Estadísticas
  const stats = useMemo(() => {
    const totalClubs = myClubs.length;
    const totalMembers = myMembers.length;
    const activeMembers = myMembers.filter((m) => m.status === "active").length;

    // Contar directores asignados
    const clubIds = myClubs.map((c) => c.id);
    const directors =
      roleAssignments?.filter(
        (r) =>
          r.role === "club_director" &&
          clubIds.includes(r.scopeId || "") &&
          r.status === "active",
      ) || [];

    // Clubes sin director
    const clubsWithoutDirector = myClubs.filter((club) => {
      const hasDirector = directors.some((d) => d.scopeId === club.id);
      return !hasDirector;
    });

    return {
      totalClubs,
      totalMembers,
      activeMembers,
      inactiveMembers: totalMembers - activeMembers,
      directorsCount: directors.length,
      clubsWithoutDirector: clubsWithoutDirector.length,
      activeRate:
        totalMembers > 0 ? Math.round((activeMembers / totalMembers) * 100) : 0,
    };
  }, [myClubs, myMembers, roleAssignments]);

  // Clubes por tipo (contando secciones activas)
  const clubsByType = useMemo(() => {
    const types: Record<string, number> = {};
    myClubs.forEach((club) => {
      const sections = getActiveSections(club);
      sections.forEach((s: string) => {
        types[s] = (types[s] || 0) + 1;
      });
    });
    return types;
  }, [myClubs]);

  const clubTypeLabels: Record<string, string> = {
    aventureros: "Aventureros",
    conquistadores: "Conquistadores",
    guias_mayores: "Guías Mayores",
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header con gradiente */}
      <div className="relative overflow-hidden bg-gradient-to-br from-indigo-600 via-indigo-500 to-purple-500 rounded-2xl p-6 text-white shadow-xl shadow-indigo-500/25">
        <div className="absolute inset-0 bg-mesh opacity-30" />
        <div className="relative flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center ring-4 ring-white/30">
              <Shield className="w-7 h-7" />
            </div>
            <div>
              <p className="text-indigo-100 text-sm font-medium mb-0.5">
                Panel de Asociación
              </p>
              <h1 className="text-2xl font-bold">
                {myAssociation?.name || "Mi Asociación"}
              </h1>
            </div>
          </div>
          {myAssociation?.region && (
            <Badge className="bg-white/20 text-white border-0 backdrop-blur-sm">
              {myAssociation.region}
            </Badge>
          )}
        </div>
      </div>

      {/* Stats Cards - Modernos */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        <Link to="/organization" className="group">
          <Card className="border-0 shadow-lg group-hover:shadow-xl group-hover:-translate-y-1 transition-all duration-300 cursor-pointer">
            <CardContent className="py-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Total Clubes
                  </p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">
                    {stats.totalClubs}
                  </p>
                </div>
                <div className="w-14 h-14 bg-gradient-to-br from-blue-400 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/30 group-hover:scale-110 transition-transform duration-300">
                  <Building2 className="w-7 h-7 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/members" className="group">
          <Card className="border-0 shadow-lg group-hover:shadow-xl group-hover:-translate-y-1 transition-all duration-300 cursor-pointer">
            <CardContent className="py-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Miembros Activos
                  </p>
                  <p className="text-3xl font-bold text-emerald-600 mt-1">
                    {stats.activeMembers}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {stats.totalMembers} total
                  </p>
                </div>
                <div className="w-14 h-14 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-500/30 group-hover:scale-110 transition-transform duration-300">
                  <Users className="w-7 h-7 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/roles" className="group">
          <Card className="border-0 shadow-lg group-hover:shadow-xl group-hover:-translate-y-1 transition-all duration-300 cursor-pointer">
            <CardContent className="py-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Directores Asignados
                  </p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">
                    {stats.directorsCount}
                  </p>
                  {stats.clubsWithoutDirector > 0 && (
                    <p className="text-xs text-orange-500 mt-1 font-medium">
                      {stats.clubsWithoutDirector} sin director
                    </p>
                  )}
                </div>
                <div className="w-14 h-14 bg-gradient-to-br from-purple-400 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg shadow-purple-500/30 group-hover:scale-110 transition-transform duration-300">
                  <Shield className="w-7 h-7 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/members" className="group">
          <Card className="border-0 shadow-lg group-hover:shadow-xl group-hover:-translate-y-1 transition-all duration-300 cursor-pointer">
            <CardContent className="py-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Tasa de Actividad
                  </p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">
                    {stats.activeRate}%
                  </p>
                  <p className="text-xs text-gray-400 mt-1">miembros activos</p>
                </div>
                <div className="w-14 h-14 bg-gradient-to-br from-indigo-400 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg shadow-indigo-500/30 group-hover:scale-110 transition-transform duration-300">
                  <TrendingUp className="w-7 h-7 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Alerta de clubes sin director - Moderno */}
      {stats.clubsWithoutDirector > 0 && (
        <Card className="border-2 border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50 shadow-md">
          <CardContent className="py-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-amber-500 rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/25">
                  <AlertCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-orange-800">
                    {stats.clubsWithoutDirector} club(es) sin director asignado
                  </p>
                  <p className="text-sm text-orange-600">
                    Asigna directores para que puedan gestionar sus clubes
                  </p>
                </div>
              </div>
              <Link
                to="/roles"
                className="inline-flex items-center gap-1 px-4 py-2 bg-orange-100 hover:bg-orange-200 text-orange-700 font-medium text-sm rounded-xl transition-colors duration-200"
              >
                Asignar directores
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Clubes por Tipo - Moderno */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {Object.entries(clubsByType).map(([type, count]) => {
          const gradients: Record<string, string> = {
            ...SECTION_GRADIENT_CLASSES,
          };
          return (
            <Card
              key={type}
              className="border-0 shadow-lg group hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
            >
              <CardContent className="py-5">
                <div className="flex items-center gap-4">
                  <div
                    className={`w-12 h-12 bg-gradient-to-br ${gradients[type] || "from-gray-400 to-gray-600"} rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}
                  >
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <SectionBadge section={type as ClubType} size="sm" />
                    <div className="flex items-baseline gap-2 mt-1">
                      <span className="text-3xl font-bold text-gray-900">
                        {count}
                      </span>
                      <span className="text-gray-500 text-sm">club(es)</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Lista de Clubes - Moderno */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center shadow-md shadow-blue-500/25">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <span>Clubes de mi Asociación</span>
            </div>
            <Link
              to="/organization"
              className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 group transition-colors duration-200"
            >
              Gestionar
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {myClubs.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-100">
                    <th className="text-left py-4 px-4 text-sm font-semibold text-gray-600">
                      Club
                    </th>
                    <th className="text-center py-4 px-4 text-sm font-semibold text-gray-600">
                      Tipo
                    </th>
                    <th className="text-center py-4 px-4 text-sm font-semibold text-gray-600">
                      Director
                    </th>
                    <th className="text-center py-4 px-4 text-sm font-semibold text-gray-600">
                      Estado
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {myClubs.map((club) => {
                    const hasDirector = roleAssignments?.some(
                      (r) =>
                        r.role === "club_director" &&
                        r.scopeId === club.id &&
                        r.status === "active",
                    );
                    const director = roleAssignments?.find(
                      (r) =>
                        r.role === "club_director" &&
                        r.scopeId === club.id &&
                        r.status === "active",
                    );

                    return (
                      <tr
                        key={club.id}
                        className="border-b border-gray-50 hover:bg-gradient-to-r hover:from-gray-50 hover:to-transparent transition-colors duration-200"
                      >
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center shadow-md shadow-blue-500/20">
                              <Building2 className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">
                                {club.name}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <Badge variant="info" size="sm">
                            {getActiveSections(club)
                              .map((s: string) => clubTypeLabels[s] || s)
                              .join(", ")}
                          </Badge>
                        </td>
                        <td className="py-4 px-4 text-center">
                          {hasDirector ? (
                            <div className="flex items-center justify-center gap-2">
                              <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center">
                                <CheckCircle className="w-4 h-4 text-emerald-500" />
                              </div>
                              <span className="text-sm text-gray-600 font-medium">
                                {director?.userName || "Asignado"}
                              </span>
                            </div>
                          ) : (
                            <div className="flex items-center justify-center gap-2">
                              <div className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center">
                                <AlertCircle className="w-4 h-4 text-orange-500" />
                              </div>
                              <span className="text-sm text-orange-600 font-medium">
                                Sin asignar
                              </span>
                            </div>
                          )}
                        </td>
                        <td className="py-4 px-4 text-center">
                          <Badge
                            variant={
                              club.status === "active" ? "success" : "secondary"
                            }
                            size="sm"
                          >
                            {club.status === "active" ? "Activo" : "Inactivo"}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <div className="w-16 h-16 bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Building2 className="w-8 h-8 text-gray-500" />
              </div>
              <p className="font-medium">
                No hay clubes registrados en tu asociación
              </p>
              <Link
                to="/organization"
                className="inline-flex items-center gap-1 mt-3 px-4 py-2 bg-primary-50 hover:bg-primary-100 text-primary-600 font-medium rounded-xl transition-colors duration-200"
              >
                Crear club
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Acciones Rápidas - Modernas */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-primary-400 to-primary-600 rounded-xl flex items-center justify-center shadow-md shadow-primary-500/25">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <CardTitle className="text-lg">Acciones Rápidas</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Link to="/organization" className="group">
              <div className="flex flex-col items-center gap-3 p-5 bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border border-slate-200">
                <div className="w-14 h-14 bg-gradient-to-br from-slate-500 to-slate-700 rounded-xl flex items-center justify-center shadow-lg shadow-slate-500/25 group-hover:scale-110 transition-transform duration-300">
                  <Building2 className="w-7 h-7 text-white" />
                </div>
                <span className="text-sm font-semibold text-gray-700">
                  Gestionar Clubes
                </span>
              </div>
            </Link>
            <Link to="/roles" className="group">
              <div className="flex flex-col items-center gap-3 p-5 bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border border-purple-200">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-700 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/25 group-hover:scale-110 transition-transform duration-300">
                  <Shield className="w-7 h-7 text-white" />
                </div>
                <span className="text-sm font-semibold text-gray-700">
                  Asignar Directores
                </span>
              </div>
            </Link>
            <Link to="/members" className="group">
              <div className="flex flex-col items-center gap-3 p-5 bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border border-blue-200">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/25 group-hover:scale-110 transition-transform duration-300">
                  <Users className="w-7 h-7 text-white" />
                </div>
                <span className="text-sm font-semibold text-gray-700">
                  Ver Miembros
                </span>
              </div>
            </Link>
            <Link to="/reports" className="group">
              <div className="flex flex-col items-center gap-3 p-5 bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-2xl hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border border-cyan-200">
                <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-cyan-700 rounded-xl flex items-center justify-center shadow-lg shadow-cyan-500/25 group-hover:scale-110 transition-transform duration-300">
                  <FileText className="w-7 h-7 text-white" />
                </div>
                <span className="text-sm font-semibold text-gray-700">
                  Reportes
                </span>
              </div>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================
// COMPONENTE: DASHBOARD PARA ADMIN
// (admin_association, club_director)
// ============================================
function AdminDashboard({
  role,
  defaultClub,
  activePeriod,
  clubs,
}: {
  role: "admin_union" | "admin_association" | "club_director";
  defaultClub:
    | { id: string; name: string; logoUrl?: string; inviteCode?: string }
    | undefined;
  activePeriod: { id: string; name: string } | undefined;
  clubs: { id: string; name: string }[] | undefined;
}) {
  const { user: adminUser } = useAuthStore();
  const { show: showOnboarding, complete: completeOnboarding } =
    useDirectorOnboarding(
      role === "club_director" ? adminUser?.uid : undefined,
    );

  const { data: members } = useMembersByClub(defaultClub?.id);
  const { data: upcomingEvents } = useUpcomingEvents();
  const { data: publishedEvents } = useEventsByStatus("published");
  const { data: clubRegistrations } = useClubRegistrations(
    role === "club_director" ? defaultClub?.id : undefined,
  );
  const { data: clubEventPayments } = useClubEventPaymentsAll(
    role === "club_director" ? defaultClub?.id : undefined,
  );
  const { data: charges } = useChargesByClubAndPeriod(
    defaultClub?.id,
    activePeriod?.id,
  );
  const { data: financialSummary } = useFinancialSummary(
    defaultClub?.id,
    activePeriod?.id,
  );
  const { data: meetings } = useMeetingsByClubAndPeriod(
    defaultClub?.id,
    activePeriod?.id,
  );
  const { data: achievements } = useAchievementsByClub(
    defaultClub?.id || "",
    activePeriod?.id,
  );

  // Calcular estadísticas
  const stats = useMemo(() => {
    const activeMembers =
      members?.filter((m) => m.status === "active").length || 0;
    const totalMembers = members?.length || 0;
    const totalAchievements = achievements?.length || 0;
    const activeEvents = publishedEvents?.length || 0;
    const pendingCharges =
      charges?.filter((c) => c.status === "pending" || c.status === "partial")
        .length || 0;
    const totalMeetings = meetings?.length || 0;
    const closedMeetings = meetings?.filter((m) => m.isClosed).length || 0;

    return {
      activeMembers,
      totalMembers,
      totalAchievements,
      activeEvents,
      pendingCharges,
      balance: financialSummary?.netBalance || 0,
      totalMeetings,
      closedMeetings,
      attendanceRate:
        closedMeetings > 0
          ? Math.round(
              (meetings
                ?.filter((m) => m.isClosed)
                .reduce((sum, m) => {
                  const rate =
                    m.totalMembers > 0
                      ? (m.attendanceCount / m.totalMembers) * 100
                      : 0;
                  return sum + rate;
                }, 0) || 0) / closedMeetings,
            )
          : 0,
    };
  }, [
    members,
    achievements,
    publishedEvents,
    charges,
    financialSummary,
    meetings,
  ]);

  // Cobros pendientes
  const pendingChargesList = useMemo(() => {
    if (!charges) return [];
    const toMs = (d: unknown): number => {
      if (!d) return 0;
      if (typeof (d as { toDate?: () => Date }).toDate === "function") {
        return (d as { toDate: () => Date }).toDate().getTime();
      }
      if (d instanceof Date) return d.getTime();
      const parsed = new Date(d as string);
      return isNaN(parsed.getTime()) ? 0 : parsed.getTime();
    };
    return charges
      .filter((c) => c.status === "pending" || c.status === "partial")
      .filter((c) => c.dueDate)
      .sort((a, b) => toMs(a.dueDate) - toMs(b.dueDate))
      .slice(0, 5);
  }, [charges]);

  // Expand/collapse de la lista completa de miembros con documentación pendiente.
  const [showAllDocs, setShowAllDocs] = useState(false);
  const DOCS_PREVIEW_LIMIT = 5;

  // Estadísticas de documentación de miembros (solo director)
  const docStats = useMemo(() => {
    if (!members || members.length === 0)
      return {
        complete: 0,
        incomplete: 0,
        avgPct: 0,
        topMissing: [] as Array<{
          memberId: string;
          personId: string;
          name: string;
          photoUrl?: string;
          missing: string[];
          pct: number;
        }>,
      };

    const checkPerson = (p: { person?: any } & any) => {
      const person = p.person || {};
      const checks: Array<{ ok: boolean; label: string }> = [
        { ok: !!person.photoUrl, label: "Foto" },
        { ok: !!person.documentFrontUrl, label: "Doc anverso" },
        { ok: !!person.documentBackUrl, label: "Doc reverso" },
        {
          ok: !!person.epsDocumentUrl || !!person.healthInsurance,
          label: "EPS",
        },
        { ok: !!person.emergencyContact?.phone, label: "Contacto emergencia" },
        { ok: !!person.address, label: "Dirección" },
        { ok: !!person.bloodType, label: "Tipo de sangre" },
      ];
      const done = checks.filter((c) => c.ok).length;
      return {
        pct: Math.round((done / checks.length) * 100),
        missing: checks.filter((c) => !c.ok).map((c) => c.label),
      };
    };

    const enriched = members.map((m: any) => {
      const r = checkPerson(m);
      return {
        memberId: m.id,
        personId: m.personId,
        name:
          m.person?.fullName ||
          `${m.person?.firstName ?? ""} ${m.person?.lastName ?? ""}`.trim() ||
          m.personName ||
          "Sin nombre",
        photoUrl: m.person?.photoUrl,
        missing: r.missing,
        pct: r.pct,
      };
    });

    const complete = enriched.filter((m) => m.pct === 100).length;
    const avgPct =
      enriched.length > 0
        ? Math.round(enriched.reduce((s, m) => s + m.pct, 0) / enriched.length)
        : 0;
    // Todos los que requieren atención (pct < 100), ordenados por mayor urgencia.
    const topMissing = enriched
      .filter((m) => m.pct < 100)
      .sort((a, b) => a.pct - b.pct);

    return {
      complete,
      incomplete: enriched.length - complete,
      avgPct,
      topMissing,
    };
  }, [members]);

  // Eventos próximos enriquecidos con stats del club (solo director)
  const clubEventsEnriched = useMemo(() => {
    if (role !== "club_director" || !publishedEvents) return [];
    const totalClubMembers =
      members?.filter((m: any) => m.status === "active").length || 0;

    const myClubId = defaultClub?.id;
    const myAssociationId = (defaultClub as any)?.associationId;

    return (
      publishedEvents
        // Filtrar por scope: el director solo ve eventos relevantes a su club.
        // - level "club":    solo si lo organiza su propio club
        // - level "association": solo si pertenece a la misma asociación
        // - level "union" / "general" / sin level: visibles
        .filter((event: any) => {
          const level = event.level || event.scope || event.scopeType;
          if (level === "club") {
            return (
              event.organizerId === myClubId ||
              event.scopeId === myClubId ||
              event.clubId === myClubId
            );
          }
          if (level === "association") {
            return (
              !!myAssociationId &&
              (event.associationId === myAssociationId ||
                event.scopeId === myAssociationId ||
                event.organizerId === myAssociationId)
            );
          }
          return true;
        })
        .map((event: any) => {
          const startDate = toSafeDate(event.startDate);
          const regEnd = toSafeDate(event.registrationConfig?.endDate);
          const regStart = toSafeDate(event.registrationConfig?.startDate);
          const now = new Date();

          const eventRegs =
            clubRegistrations?.filter(
              (r) =>
                r.eventId === event.id &&
                r.status !== "cancelled" &&
                r.status !== "rejected",
            ) || [];

          const registeredCount = eventRegs.length;
          const totalDue = eventRegs.reduce(
            (s, r) => s + (r.unitPrice ?? 0),
            0,
          );
          // Pagado real: suma de pagos APROBADOS del club para este evento
          // (no se basa en r.amountPaid que puede estar desactualizado)
          const totalPaid = (clubEventPayments ?? [])
            .filter((p) => p.eventId === event.id && p.status === "approved")
            .reduce((s, p) => s + (p.amount ?? 0), 0);
          const totalPending = Math.max(0, totalDue - totalPaid);

          // Monto en revisión (pendiente de aprobación)
          const totalInReview = (clubEventPayments ?? [])
            .filter((p) => p.eventId === event.id && p.status === "pending")
            .reduce((s, p) => s + (p.amount ?? 0), 0);

          let regStatus: "not_open" | "open" | "closed" = "not_open";
          let regLabel = "";
          if (now < regStart) {
            const days = Math.ceil(
              (regStart.getTime() - now.getTime()) / 86400000,
            );
            regStatus = "not_open";
            regLabel = `Abre en ${days}d`;
          } else if (now > regEnd) {
            regStatus = "closed";
            regLabel = "Cerrado";
          } else {
            const days = Math.ceil(
              (regEnd.getTime() - now.getTime()) / 86400000,
            );
            regStatus = "open";
            regLabel = days > 1 ? `Cierra en ${days}d` : "Cierra hoy";
          }

          const daysToEvent = Math.ceil(
            (startDate.getTime() - now.getTime()) / 86400000,
          );

          return {
            event,
            startDate,
            daysToEvent,
            regStatus,
            regLabel,
            registeredCount,
            totalClubMembers,
            totalDue,
            totalPaid,
            totalPending,
            totalInReview,
            coveragePct:
              totalClubMembers > 0
                ? Math.round((registeredCount / totalClubMembers) * 100)
                : 0,
            paidPct:
              totalDue > 0 ? Math.round((totalPaid / totalDue) * 100) : 0,
          };
        })
        .filter((x) => x.daysToEvent >= -1) // incluye recién finalizados
        .sort((a, b) => a.daysToEvent - b.daysToEvent)
    );
  }, [
    role,
    publishedEvents,
    clubRegistrations,
    clubEventPayments,
    members,
    defaultClub?.id,
    defaultClub,
  ]);

  // Título según rol
  const getRoleTitle = () => {
    switch (role) {
      case "admin_union":
        return "Panel de Administración - Unión";
      case "admin_association":
        return "Panel de Administración - Asociación";
      case "club_director":
        return "Panel del Director";
    }
  };

  const getRoleSubtitle = () => {
    if (defaultClub) {
      return `Gestión de ${defaultClub.name}`;
    }
    if (role === "admin_union") {
      return `${clubs?.length || 0} clubes en el sistema`;
    }
    if (role === "admin_association") {
      return `${clubs?.length || 0} clubes en tu asociación`;
    }
    return "Sin club asignado";
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Onboarding para director */}
      {showOnboarding && <DirectorOnboarding onClose={completeOnboarding} />}

      {/* Header con gradiente */}
      <div className="relative overflow-hidden bg-gradient-to-br from-primary-600 via-primary-500 to-accent-500 rounded-2xl p-8 text-white shadow-xl shadow-primary-500/25">
        <div className="absolute inset-0 bg-mesh opacity-30" />
        <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-5">
            {defaultClub?.logoUrl ? (
              <img
                src={defaultClub.logoUrl}
                alt={defaultClub.name}
                className="w-16 h-16 rounded-2xl object-cover ring-4 ring-white/30"
              />
            ) : (
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center ring-4 ring-white/30">
                <Shield className="w-8 h-8" />
              </div>
            )}
            <div>
              <p className="text-primary-100 text-sm font-medium mb-1">
                Bienvenido
              </p>
              <h1 className="text-2xl md:text-3xl font-bold">
                {getRoleTitle()}
              </h1>
              <p className="text-primary-100 mt-1 flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                {getRoleSubtitle()}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {activePeriod && (
              <Badge className="bg-white/20 text-white border-0 backdrop-blur-sm">
                Periodo: {activePeriod.name}
              </Badge>
            )}
            {role !== "club_director" && clubs && clubs.length > 1 && (
              <Badge className="bg-white/20 text-white border-0 backdrop-blur-sm">
                {clubs.length} clubes
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Código de invitación para directores */}
      {role === "club_director" && defaultClub?.inviteCode && (
        <InviteCodeBanner inviteCode={defaultClub.inviteCode} />
      )}

      {/* ============================================
          EVENTOS — Acciones pendientes (compacto)
          Solo aparece si hay eventos que requieren acción del director:
          - Inscripciones abiertas con miembros aún sin inscribir
          - Pagos pendientes (deuda real)
          ============================================ */}
      {role === "club_director" &&
        (() => {
          const actionable = clubEventsEnriched.filter((e) => {
            const needsRegister =
              e.regStatus === "open" &&
              e.registeredCount < e.totalClubMembers &&
              e.daysToEvent >= 0;
            const needsPayment = e.totalPending > 0;
            return needsRegister || needsPayment;
          });
          if (actionable.length === 0) return null;
          return (
            <Card className="border-0 shadow-md overflow-hidden">
              <div className="px-5 py-3 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-100 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                    <Calendar className="w-4 h-4 text-amber-700" />
                  </div>
                  <div>
                    <h2 className="text-sm font-bold text-gray-900">
                      Acciones pendientes en eventos
                    </h2>
                    <p className="text-[11px] text-gray-500">
                      {actionable.length} evento
                      {actionable.length !== 1 ? "s" : ""} requiere
                      {actionable.length !== 1 ? "n" : ""} tu atención
                    </p>
                  </div>
                </div>
                <Link
                  to="/events"
                  className="text-xs font-semibold text-primary-600 hover:text-primary-700 flex items-center gap-1"
                >
                  Ver todos
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>

              <div className="divide-y divide-gray-100">
                {actionable.slice(0, 5).map((e) => {
                  const missing = Math.max(
                    0,
                    e.totalClubMembers - e.registeredCount,
                  );
                  const urgent = e.regStatus === "open" && e.daysToEvent <= 14;
                  return (
                    <div
                      key={e.event.id}
                      className="px-5 py-3 flex flex-col sm:flex-row sm:items-center gap-3 hover:bg-gray-50 transition-colors"
                    >
                      {/* Fecha + nombre */}
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div
                          className={`flex-shrink-0 w-11 h-11 rounded-xl flex flex-col items-center justify-center text-white text-center ${
                            urgent
                              ? "bg-gradient-to-br from-orange-500 to-amber-500"
                              : "bg-gradient-to-br from-primary-500 to-accent-500"
                          }`}
                        >
                          <p className="text-base font-bold leading-none">
                            {e.startDate.getDate()}
                          </p>
                          <p className="text-[9px] uppercase font-semibold opacity-90">
                            {e.startDate.toLocaleDateString("es-CO", {
                              month: "short",
                            })}
                          </p>
                        </div>
                        <div className="min-w-0 flex-1">
                          <Link
                            to={`/events/${e.event.id}`}
                            className="text-sm font-semibold text-gray-900 hover:text-primary-700 truncate block"
                          >
                            {e.event.name}
                          </Link>
                          <div className="flex items-center gap-2 flex-wrap mt-0.5">
                            <span
                              className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                                urgent
                                  ? "bg-orange-100 text-orange-700"
                                  : "bg-blue-50 text-blue-700"
                              }`}
                            >
                              {e.regLabel}
                            </span>
                            {missing > 0 && e.regStatus === "open" && (
                              <span className="text-[11px] text-amber-700 font-medium flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                {missing} sin inscribir
                              </span>
                            )}
                            {e.totalPending > 0 && (
                              <span className="text-[11px] text-red-600 font-medium flex items-center gap-1">
                                <DollarSign className="w-3 h-3" />
                                Debes {formatCurrency(e.totalPending)}
                              </span>
                            )}
                            {e.totalInReview > 0 && (
                              <span
                                className="text-[11px] text-blue-600 font-medium"
                                title="Recibos esperando aprobación"
                              >
                                · {formatCurrency(e.totalInReview)} en revisión
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Acciones */}
                      <div className="flex items-center gap-1.5 flex-shrink-0 sm:ml-auto">
                        {e.regStatus === "open" && missing > 0 && (
                          <Link
                            to={`/events/${e.event.id}`}
                            className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white transition-colors"
                          >
                            Inscribir
                          </Link>
                        )}
                        {e.totalPending > 0 && (
                          <Link
                            to={`/events/${e.event.id}`}
                            className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-red-200 text-red-700 hover:bg-red-50 transition-colors"
                          >
                            Pagar
                          </Link>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {actionable.length > 5 && (
                <div className="px-5 py-2 bg-gray-50 border-t border-gray-100 text-center">
                  <Link
                    to="/events"
                    className="text-xs font-semibold text-primary-600 hover:text-primary-700"
                  >
                    Ver {actionable.length - 5} evento
                    {actionable.length - 5 !== 1 ? "s" : ""} más
                  </Link>
                </div>
              )}
            </Card>
          );
        })()}

      {/* Stats Grid - Modernos con hover */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        <Link to="/members" className="group">
          <Card className="border-0 shadow-lg group-hover:shadow-xl group-hover:-translate-y-1 transition-all duration-300 cursor-pointer">
            <CardContent className="py-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Miembros Activos
                  </p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">
                    {stats.activeMembers}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {stats.totalMembers} total
                  </p>
                </div>
                <div className="w-14 h-14 bg-gradient-to-br from-blue-400 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/30 group-hover:scale-110 transition-transform duration-300">
                  <Users className="w-7 h-7 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/progress" className="group">
          <Card className="border-0 shadow-lg group-hover:shadow-xl group-hover:-translate-y-1 transition-all duration-300 cursor-pointer">
            <CardContent className="py-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Especialidades
                  </p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">
                    {stats.totalAchievements}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">Este periodo</p>
                </div>
                <div className="w-14 h-14 bg-gradient-to-br from-purple-400 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg shadow-purple-500/30 group-hover:scale-110 transition-transform duration-300">
                  <Award className="w-7 h-7 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/events" className="group">
          <Card className="border-0 shadow-lg group-hover:shadow-xl group-hover:-translate-y-1 transition-all duration-300 cursor-pointer">
            <CardContent className="py-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Eventos Activos
                  </p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">
                    {stats.activeEvents}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">Publicados</p>
                </div>
                <div className="w-14 h-14 bg-gradient-to-br from-orange-400 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg shadow-orange-500/30 group-hover:scale-110 transition-transform duration-300">
                  <Calendar className="w-7 h-7 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/finance" className="group">
          <Card className="border-0 shadow-lg group-hover:shadow-xl group-hover:-translate-y-1 transition-all duration-300 cursor-pointer">
            <CardContent className="py-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Balance</p>
                  <p
                    className={`text-3xl font-bold mt-1 ${stats.balance >= 0 ? "text-emerald-600" : "text-red-600"}`}
                  >
                    {formatCurrency(stats.balance)}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {stats.pendingCharges} cobros pendientes
                  </p>
                </div>
                <div
                  className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300 ${
                    stats.balance >= 0
                      ? "bg-gradient-to-br from-emerald-400 to-emerald-600 shadow-emerald-500/30"
                      : "bg-gradient-to-br from-red-400 to-red-600 shadow-red-500/30"
                  }`}
                >
                  <DollarSign className="w-7 h-7 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* ===== EVENTO DESTACADO ===== */}
      {role !== "club_director" &&
        (() => {
          // Elegir el evento más relevante: el próximo publicado con inscripciones abiertas o por abrir
          const featuredEvent = publishedEvents?.sort((a, b) => {
            const aStart = toSafeDate(a.startDate).getTime();
            const bStart = toSafeDate(b.startDate).getTime();
            return aStart - bStart;
          })[0];

          if (!featuredEvent) return null;

          const startDate = toSafeDate(featuredEvent.startDate);
          const endDate = toSafeDate(featuredEvent.endDate);
          const regStart = toSafeDate(
            featuredEvent.registrationConfig?.startDate,
          );
          const regEnd = toSafeDate(featuredEvent.registrationConfig?.endDate);
          const now = new Date();

          // Ventana de inscripción
          let regStatus: "not_open" | "open" | "closed" = "not_open";
          let regLabel = "";
          if (now < regStart) {
            const diffMs = regStart.getTime() - now.getTime();
            const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
            const opensAt = regStart.toLocaleDateString("es-CO", {
              day: "numeric",
              month: "short",
              hour: "2-digit",
              minute: "2-digit",
            });
            regStatus = "not_open";
            regLabel =
              diffDays > 1
                ? `Abre en ${diffDays} días (${opensAt})`
                : `Abre pronto (${opensAt})`;
          } else if (now > regEnd) {
            regStatus = "closed";
            regLabel = "Inscripciones cerradas";
          } else {
            const diffMs = regEnd.getTime() - now.getTime();
            const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
            regStatus = "open";
            regLabel =
              diffDays > 1 ? `Cierra en ${diffDays} días` : "Cierra pronto";
          }

          // Pronto pago activo?
          const hasEarlyBird = featuredEvent.pricing?.some(
            (p) =>
              p.earlyBirdAmount != null &&
              p.earlyBirdDeadline &&
              now <= toSafeDate(p.earlyBirdDeadline),
          );

          // Días para el evento
          const daysToEvent = Math.ceil(
            (startDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
          );

          return (
            <Card className="border-0 shadow-xl overflow-hidden">
              {/* Hero gradient header */}
              <div className="relative bg-gradient-to-br from-orange-500 via-orange-400 to-amber-400 p-6 text-white">
                <div className="absolute inset-0 bg-mesh opacity-20" />
                <div className="relative">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className="bg-white/20 text-white border-0 backdrop-blur-sm text-xs">
                          {featuredEvent.eventType}
                        </Badge>
                        {daysToEvent > 0 && (
                          <Badge className="bg-white/20 text-white border-0 backdrop-blur-sm text-xs">
                            Faltan {daysToEvent} día
                            {daysToEvent !== 1 ? "s" : ""}
                          </Badge>
                        )}
                        {hasEarlyBird && (
                          <Badge className="bg-green-500/80 text-white border-0 backdrop-blur-sm text-xs animate-pulse">
                            💰 Pronto pago activo
                          </Badge>
                        )}
                      </div>
                      <h2 className="text-2xl md:text-3xl font-bold mb-1">
                        {featuredEvent.name}
                      </h2>
                      <p className="text-white/80 text-sm">
                        Organiza: {featuredEvent.organizerName}
                      </p>
                    </div>
                    <div className="flex-shrink-0 w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex flex-col items-center justify-center ring-4 ring-white/30">
                      <p className="text-3xl font-bold leading-none">
                        {startDate.getDate()}
                      </p>
                      <p className="text-xs uppercase font-medium opacity-90">
                        {startDate.toLocaleDateString("es-CO", {
                          month: "short",
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <CardContent className="p-6">
                {/* Info grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
                  {/* Fecha */}
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-lg flex items-center justify-center shadow-md">
                      <Calendar className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Fecha</p>
                      <p className="text-sm font-semibold text-gray-900">
                        {startDate.toLocaleDateString("es-CO", {
                          day: "numeric",
                          month: "long",
                        })}
                        {startDate.toDateString() !==
                          endDate.toDateString() && (
                          <>
                            {" "}
                            —{" "}
                            {endDate.toLocaleDateString("es-CO", {
                              day: "numeric",
                              month: "long",
                            })}
                          </>
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Lugar */}
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-lg flex items-center justify-center shadow-md">
                      <MapPin className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Lugar</p>
                      <p className="text-sm font-semibold text-gray-900">
                        {featuredEvent.location?.city}
                      </p>
                      {featuredEvent.location?.name && (
                        <p className="text-xs text-gray-500">
                          {featuredEvent.location.name}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Inscritos */}
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-purple-600 rounded-lg flex items-center justify-center shadow-md">
                      <Users className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-medium">
                        Inscritos
                      </p>
                      <p className="text-sm font-semibold text-gray-900">
                        {featuredEvent.registeredCount || 0}
                        {featuredEvent.registrationConfig?.maxCapacity
                          ? ` / ${featuredEvent.registrationConfig.maxCapacity}`
                          : ""}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Precios */}
                {featuredEvent.pricing && featuredEvent.pricing.length > 0 && (
                  <div className="mb-5">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      Precios
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {featuredEvent.pricing.map((p) => {
                        const ebActive =
                          p.earlyBirdAmount != null &&
                          p.earlyBirdDeadline &&
                          now <= toSafeDate(p.earlyBirdDeadline);
                        return (
                          <div
                            key={p.category}
                            className={`px-3 py-2 rounded-xl text-sm ${
                              ebActive
                                ? "bg-green-50 ring-1 ring-green-200"
                                : "bg-gray-50"
                            }`}
                          >
                            <span className="text-gray-600">{p.label}: </span>
                            {ebActive ? (
                              <>
                                <span className="font-bold text-green-700">
                                  ${p.earlyBirdAmount!.toLocaleString("es-CO")}
                                </span>
                                <span className="text-gray-400 line-through text-xs ml-1">
                                  ${p.amount.toLocaleString("es-CO")}
                                </span>
                              </>
                            ) : (
                              <span className="font-bold text-gray-900">
                                ${p.amount.toLocaleString("es-CO")}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Ventana de inscripción */}
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <span
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium ${
                      regStatus === "open"
                        ? "bg-green-100 text-green-700"
                        : regStatus === "not_open"
                          ? "bg-blue-100 text-blue-700"
                          : "bg-red-100 text-red-700"
                    }`}
                  >
                    {regStatus === "open" ? (
                      <Clock className="w-4 h-4" />
                    ) : (
                      <Ticket className="w-4 h-4" />
                    )}
                    {regLabel}
                  </span>

                  <Link
                    to={`/events/${featuredEvent.id}`}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white rounded-xl text-sm font-medium shadow-md shadow-orange-500/25 transition-all duration-200 hover:shadow-lg"
                  >
                    Ver evento completo
                    <ExternalLink className="w-4 h-4" />
                  </Link>
                </div>
              </CardContent>
            </Card>
          );
        })()}

      {/* ============================================
          SECCIONES EXCLUSIVAS DEL DIRECTOR DE CLUB
          ============================================ */}
      {role === "club_director" && (
        <>
          {/* Resumen del periodo: asistencia, reuniones, especialidades */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {/* Asistencia promedio */}
            <Card className="border-0 shadow-lg overflow-hidden">
              <div className="bg-gradient-to-br from-indigo-500 to-purple-600 px-5 py-4 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-indigo-100 uppercase tracking-wide">
                      Asistencia promedio
                    </p>
                    <p className="text-3xl font-bold mt-1">
                      {stats.attendanceRate}%
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                    <CheckCircle className="w-6 h-6" />
                  </div>
                </div>
              </div>
              <CardContent className="py-4">
                <div className="w-full bg-gray-100 rounded-full h-2 mb-2 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-indigo-500 to-purple-600 transition-all duration-500"
                    style={{ width: `${stats.attendanceRate}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500">
                  Basado en {stats.closedMeetings} reuniones cerradas
                </p>
              </CardContent>
            </Card>

            {/* Reuniones */}
            <Link to="/attendance" className="group">
              <Card className="border-0 shadow-lg overflow-hidden h-full group-hover:shadow-xl group-hover:-translate-y-1 transition-all duration-300">
                <div className="bg-gradient-to-br from-cyan-500 to-blue-600 px-5 py-4 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-cyan-100 uppercase tracking-wide">
                        Reuniones del periodo
                      </p>
                      <p className="text-3xl font-bold mt-1">
                        {stats.closedMeetings}
                        <span className="text-lg font-medium text-cyan-100">
                          /{stats.totalMeetings}
                        </span>
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                      <Calendar className="w-6 h-6" />
                    </div>
                  </div>
                </div>
                <CardContent className="py-4 flex items-center justify-between">
                  <p className="text-xs text-gray-500">
                    {stats.totalMeetings - stats.closedMeetings > 0
                      ? `${stats.totalMeetings - stats.closedMeetings} sin cerrar`
                      : "Todo al día"}
                  </p>
                  <ArrowRight className="w-4 h-4 text-gray-400 group-hover:translate-x-0.5 group-hover:text-primary-600 transition-all" />
                </CardContent>
              </Card>
            </Link>

            {/* Especialidades / Progreso */}
            <Link to="/progress" className="group">
              <Card className="border-0 shadow-lg overflow-hidden h-full group-hover:shadow-xl group-hover:-translate-y-1 transition-all duration-300">
                <div className="bg-gradient-to-br from-pink-500 to-rose-600 px-5 py-4 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-pink-100 uppercase tracking-wide">
                        Especialidades del periodo
                      </p>
                      <p className="text-3xl font-bold mt-1">
                        {stats.totalAchievements}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                      <Award className="w-6 h-6" />
                    </div>
                  </div>
                </div>
                <CardContent className="py-4 flex items-center justify-between">
                  <p className="text-xs text-gray-500">Registrar nuevo logro</p>
                  <ArrowRight className="w-4 h-4 text-gray-400 group-hover:translate-x-0.5 group-hover:text-primary-600 transition-all" />
                </CardContent>
              </Card>
            </Link>
          </div>

          {/* Documentación de miembros - acceso directo a documentos */}
          <Card className="border-0 shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800 px-6 py-5 text-white">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-white/15 backdrop-blur-sm rounded-2xl flex items-center justify-center ring-2 ring-white/20">
                    <FileText className="w-7 h-7" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold">
                      Documentación del club
                    </h3>
                    <p className="text-sm text-slate-300">
                      Foto, documento de identidad, EPS y datos críticos
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold">{docStats.complete}</p>
                    <p className="text-[10px] uppercase text-slate-300 font-medium">
                      Completos
                    </p>
                  </div>
                  <div className="w-px h-10 bg-white/20" />
                  <button
                    type="button"
                    onClick={() => {
                      if (docStats.incomplete === 0) return;
                      setShowAllDocs(true);
                      requestAnimationFrame(() => {
                        document
                          .getElementById("doc-pending-list")
                          ?.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                          });
                      });
                    }}
                    className={`text-center rounded-lg px-2 py-1 transition-colors ${
                      docStats.incomplete > 0
                        ? "hover:bg-white/10 cursor-pointer"
                        : "cursor-default"
                    }`}
                    title={
                      docStats.incomplete > 0
                        ? `Ver los ${docStats.incomplete} pendientes`
                        : "Sin pendientes"
                    }
                  >
                    <p
                      className={`text-2xl font-bold ${
                        docStats.incomplete > 0
                          ? "text-amber-300"
                          : "text-emerald-300"
                      }`}
                    >
                      {docStats.incomplete}
                    </p>
                    <p className="text-[10px] uppercase text-slate-300 font-medium">
                      Pendientes
                    </p>
                  </button>
                  <div className="w-px h-10 bg-white/20" />
                  <div className="text-center">
                    <p className="text-2xl font-bold">{docStats.avgPct}%</p>
                    <p className="text-[10px] uppercase text-slate-300 font-medium">
                      Promedio
                    </p>
                  </div>
                </div>
              </div>
              {/* Barra de progreso global */}
              <div className="mt-4 w-full bg-white/10 rounded-full h-2 overflow-hidden">
                <div
                  className={`h-full transition-all duration-500 ${
                    docStats.avgPct >= 80
                      ? "bg-gradient-to-r from-emerald-400 to-emerald-300"
                      : docStats.avgPct >= 50
                        ? "bg-gradient-to-r from-amber-400 to-amber-300"
                        : "bg-gradient-to-r from-red-400 to-red-300"
                  }`}
                  style={{ width: `${docStats.avgPct}%` }}
                />
              </div>
            </div>

            <CardContent className="p-6">
              {docStats.topMissing.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-emerald-100 to-emerald-200 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg">
                    <CheckCircle className="w-8 h-8 text-emerald-600" />
                  </div>
                  <p className="font-semibold text-emerald-700">
                    ¡Documentación completa!
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    Todos los miembros tienen sus datos al día.
                  </p>
                </div>
              ) : (
                <div id="doc-pending-list">
                  <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                    <div>
                      <p className="text-sm font-semibold text-gray-700">
                        Miembros que requieren atención
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {showAllDocs ||
                        docStats.topMissing.length <= DOCS_PREVIEW_LIMIT
                          ? `${docStats.topMissing.length} en total, ordenados por urgencia`
                          : `Mostrando ${DOCS_PREVIEW_LIMIT} de ${docStats.topMissing.length}`}
                      </p>
                    </div>
                    <Link
                      to="/members"
                      className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 group"
                    >
                      Ver todos los miembros
                      <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                    </Link>
                  </div>
                  <div className="space-y-2.5">
                    {(showAllDocs
                      ? docStats.topMissing
                      : docStats.topMissing.slice(0, DOCS_PREVIEW_LIMIT)
                    ).map((m) => (
                      <Link
                        key={m.memberId}
                        to={`/members/${m.personId}`}
                        className="flex items-center gap-3 p-3 bg-gradient-to-r from-amber-50/60 to-white rounded-xl border border-amber-100 hover:border-amber-300 hover:shadow-md transition-all duration-200 group"
                      >
                        {m.photoUrl ? (
                          <img
                            src={m.photoUrl}
                            alt={m.name}
                            className="w-10 h-10 rounded-xl object-cover ring-2 ring-amber-200"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-xl bg-amber-100 ring-2 ring-amber-200 flex items-center justify-center">
                            <User className="w-5 h-5 text-amber-700" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-900 truncate">
                            {m.name}
                          </p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {m.missing.slice(0, 4).map((field) => (
                              <span
                                key={field}
                                className="text-[10px] px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full font-medium"
                              >
                                {field}
                              </span>
                            ))}
                            {m.missing.length > 4 && (
                              <span className="text-[10px] px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full font-medium">
                                +{m.missing.length - 4}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <div className="text-right">
                            <p
                              className={`text-sm font-bold ${
                                m.pct < 50
                                  ? "text-red-600"
                                  : m.pct < 80
                                    ? "text-amber-600"
                                    : "text-emerald-600"
                              }`}
                            >
                              {m.pct}%
                            </p>
                          </div>
                          <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-primary-600 group-hover:translate-x-0.5 transition-all" />
                        </div>
                      </Link>
                    ))}
                  </div>

                  {docStats.topMissing.length > DOCS_PREVIEW_LIMIT && (
                    <button
                      type="button"
                      onClick={() => setShowAllDocs((v) => !v)}
                      className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-dashed border-amber-300 bg-amber-50/40 hover:bg-amber-50 text-sm font-semibold text-amber-800 transition-colors"
                    >
                      {showAllDocs ? (
                        <>
                          <ChevronUp className="w-4 h-4" />
                          Mostrar solo los {DOCS_PREVIEW_LIMIT} más urgentes
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-4 h-4" />
                          Ver los{" "}
                          {docStats.topMissing.length - DOCS_PREVIEW_LIMIT}{" "}
                          restantes
                        </>
                      )}
                    </button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upcoming Events (otros) */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl flex items-center justify-center shadow-md shadow-purple-500/25">
                <Calendar className="w-5 h-5 text-white" />
              </div>
              <CardTitle className="text-lg">Otros Eventos</CardTitle>
            </div>
            <Link
              to="/events"
              className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1 font-medium group transition-colors duration-200"
            >
              Ver todos{" "}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </CardHeader>
          <CardContent>
            {upcomingEvents && upcomingEvents.length > 1 ? (
              <div className="space-y-3">
                {upcomingEvents
                  .filter(
                    (e) =>
                      e.id !==
                      publishedEvents?.sort(
                        (a, b) =>
                          toSafeDate(a.startDate).getTime() -
                          toSafeDate(b.startDate).getTime(),
                      )[0]?.id,
                  )
                  .map((event) => (
                    <Link
                      key={event.id}
                      to={`/events/${event.id}`}
                      className="flex items-center gap-4 p-4 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100 hover:shadow-md hover:border-primary-200 transition-all duration-300"
                    >
                      <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-primary-500 to-accent-500 rounded-xl flex flex-col items-center justify-center text-white shadow-md">
                        <p className="text-lg font-bold leading-none">
                          {toSafeDate(event.startDate).getDate()}
                        </p>
                        <p className="text-[10px] uppercase font-medium opacity-90">
                          {toSafeDate(event.startDate).toLocaleDateString(
                            "es-CO",
                            { month: "short" },
                          )}
                        </p>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 truncate">
                          {event.name}
                        </p>
                        <p className="text-sm text-gray-500">
                          {event.location?.city}
                        </p>
                      </div>
                      <Badge variant="info" className="text-xs">
                        {event.eventType}
                      </Badge>
                    </Link>
                  ))}
              </div>
            ) : (
              <div className="text-center py-10 text-gray-500">
                <div className="w-16 h-16 bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg">
                  <Calendar className="w-8 h-8 text-gray-500" />
                </div>
                <p className="font-medium">No hay otros eventos próximos</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pending Payments - Moderno */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center shadow-md shadow-orange-500/25">
                <DollarSign className="w-5 h-5 text-white" />
              </div>
              <CardTitle className="text-lg">Cobros Pendientes</CardTitle>
            </div>
            <Link
              to="/finance"
              className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1 font-medium group transition-colors duration-200"
            >
              Ver todos{" "}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </CardHeader>
          <CardContent>
            {pendingChargesList.length > 0 ? (
              <div className="space-y-3">
                {pendingChargesList.map((charge) => {
                  const dueDateObj = charge.dueDate
                    ? typeof (charge.dueDate as { toDate?: () => Date })
                        .toDate === "function"
                      ? (charge.dueDate as { toDate: () => Date }).toDate()
                      : charge.dueDate instanceof Date
                        ? charge.dueDate
                        : new Date(charge.dueDate as unknown as string)
                    : null;
                  const isOverdue =
                    dueDateObj instanceof Date &&
                    !isNaN(dueDateObj.getTime()) &&
                    dueDateObj < new Date();
                  return (
                    <div
                      key={charge.id}
                      className={`flex items-center gap-4 p-4 rounded-xl border transition-all duration-300 hover:shadow-md ${
                        isOverdue
                          ? "bg-red-50/50 border-red-200 hover:border-red-300"
                          : "bg-amber-50/50 border-amber-200 hover:border-amber-300"
                      }`}
                    >
                      <div
                        className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-md ${
                          isOverdue
                            ? "bg-gradient-to-br from-red-400 to-red-600 shadow-red-500/25"
                            : "bg-gradient-to-br from-amber-400 to-amber-600 shadow-amber-500/25"
                        }`}
                      >
                        {isOverdue ? (
                          <AlertCircle className="w-5 h-5 text-white" />
                        ) : (
                          <Clock className="w-5 h-5 text-white" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 truncate">
                          {charge.conceptName}
                        </p>
                        <p className="text-sm text-gray-500">
                          {charge.personName}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-gray-900">
                          {formatCurrency(charge.balance)}
                        </p>
                        <Badge
                          variant={isOverdue ? "danger" : "warning"}
                          size="xs"
                        >
                          {isOverdue
                            ? "Vencido"
                            : dueDateObj
                              ? dueDateObj.toLocaleDateString("es-CO")
                              : ""}
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-10 text-gray-500">
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-100 to-emerald-200 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg">
                  <CheckCircle className="w-8 h-8 text-emerald-500" />
                </div>
                <p className="font-medium text-emerald-700">
                  No hay cobros pendientes
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions - Modernas con gradientes */}
        <Card className="lg:col-span-2 border-0 shadow-lg">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-primary-400 to-primary-600 rounded-xl flex items-center justify-center shadow-md shadow-primary-500/25">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <CardTitle className="text-lg">Acciones Rápidas</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {/* Acciones para UNIÓN - solo administrativas */}
            {role === "admin_union" && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Link to="/organization" className="group">
                  <div className="flex flex-col items-center gap-3 p-5 bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border border-slate-200">
                    <div className="w-14 h-14 bg-gradient-to-br from-slate-500 to-slate-700 rounded-xl flex items-center justify-center shadow-lg shadow-slate-500/25 group-hover:scale-110 transition-transform duration-300">
                      <Building2 className="w-7 h-7 text-white" />
                    </div>
                    <span className="text-sm font-semibold text-gray-700">
                      Asociaciones
                    </span>
                  </div>
                </Link>
                <Link to="/users" className="group">
                  <div className="flex flex-col items-center gap-3 p-5 bg-gradient-to-br from-violet-50 to-violet-100 rounded-2xl hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border border-violet-200">
                    <div className="w-14 h-14 bg-gradient-to-br from-violet-500 to-violet-700 rounded-xl flex items-center justify-center shadow-lg shadow-violet-500/25 group-hover:scale-110 transition-transform duration-300">
                      <Shield className="w-7 h-7 text-white" />
                    </div>
                    <span className="text-sm font-semibold text-gray-700">
                      Usuarios
                    </span>
                  </div>
                </Link>
                <Link to="/roles" className="group">
                  <div className="flex flex-col items-center gap-3 p-5 bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border border-purple-200">
                    <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-700 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/25 group-hover:scale-110 transition-transform duration-300">
                      <Users className="w-7 h-7 text-white" />
                    </div>
                    <span className="text-sm font-semibold text-gray-700">
                      Asignar Roles
                    </span>
                  </div>
                </Link>
                <Link to="/reports" className="group">
                  <div className="flex flex-col items-center gap-3 p-5 bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-2xl hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border border-cyan-200">
                    <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-cyan-700 rounded-xl flex items-center justify-center shadow-lg shadow-cyan-500/25 group-hover:scale-110 transition-transform duration-300">
                      <FileText className="w-7 h-7 text-white" />
                    </div>
                    <span className="text-sm font-semibold text-gray-700">
                      Reportes
                    </span>
                  </div>
                </Link>
              </div>
            )}

            {/* Acciones para ASOCIACIÓN */}
            {role === "admin_association" && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Link to="/organization" className="group">
                  <div className="flex flex-col items-center gap-3 p-5 bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border border-slate-200">
                    <div className="w-14 h-14 bg-gradient-to-br from-slate-500 to-slate-700 rounded-xl flex items-center justify-center shadow-lg shadow-slate-500/25 group-hover:scale-110 transition-transform duration-300">
                      <Building2 className="w-7 h-7 text-white" />
                    </div>
                    <span className="text-sm font-semibold text-gray-700">
                      Clubes
                    </span>
                  </div>
                </Link>
                <Link to="/roles" className="group">
                  <div className="flex flex-col items-center gap-3 p-5 bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border border-purple-200">
                    <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-700 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/25 group-hover:scale-110 transition-transform duration-300">
                      <Shield className="w-7 h-7 text-white" />
                    </div>
                    <span className="text-sm font-semibold text-gray-700">
                      Asignar Directores
                    </span>
                  </div>
                </Link>
                <Link to="/members" className="group">
                  <div className="flex flex-col items-center gap-3 p-5 bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border border-blue-200">
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/25 group-hover:scale-110 transition-transform duration-300">
                      <Users className="w-7 h-7 text-white" />
                    </div>
                    <span className="text-sm font-semibold text-gray-700">
                      Ver Miembros
                    </span>
                  </div>
                </Link>
                <Link to="/reports" className="group">
                  <div className="flex flex-col items-center gap-3 p-5 bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-2xl hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border border-cyan-200">
                    <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-cyan-700 rounded-xl flex items-center justify-center shadow-lg shadow-cyan-500/25 group-hover:scale-110 transition-transform duration-300">
                      <FileText className="w-7 h-7 text-white" />
                    </div>
                    <span className="text-sm font-semibold text-gray-700">
                      Reportes
                    </span>
                  </div>
                </Link>
              </div>
            )}

            {/* Acciones para DIRECTOR DE CLUB - operativas del club */}
            {role === "club_director" && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Link to="/members" className="group relative">
                  {docStats.incomplete > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 z-10 min-w-[22px] h-[22px] px-1.5 bg-amber-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center shadow-md ring-2 ring-white">
                      {docStats.incomplete}
                    </span>
                  )}
                  <div className="flex flex-col items-center gap-3 p-5 bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border border-blue-200 h-full">
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/25 group-hover:scale-110 transition-transform duration-300">
                      <Users className="w-7 h-7 text-white" />
                    </div>
                    <span className="text-sm font-semibold text-gray-700 text-center">
                      Miembros
                    </span>
                  </div>
                </Link>

                <Link to="/attendance" className="group">
                  <div className="flex flex-col items-center gap-3 p-5 bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-2xl hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border border-indigo-200 h-full">
                    <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-indigo-700 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/25 group-hover:scale-110 transition-transform duration-300">
                      <CheckCircle className="w-7 h-7 text-white" />
                    </div>
                    <span className="text-sm font-semibold text-gray-700 text-center">
                      Asistencia
                    </span>
                  </div>
                </Link>

                <Link to="/progress" className="group">
                  <div className="flex flex-col items-center gap-3 p-5 bg-gradient-to-br from-pink-50 to-pink-100 rounded-2xl hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border border-pink-200 h-full">
                    <div className="w-14 h-14 bg-gradient-to-br from-pink-500 to-pink-700 rounded-xl flex items-center justify-center shadow-lg shadow-pink-500/25 group-hover:scale-110 transition-transform duration-300">
                      <Award className="w-7 h-7 text-white" />
                    </div>
                    <span className="text-sm font-semibold text-gray-700 text-center">
                      Especialidades
                    </span>
                  </div>
                </Link>

                <Link to="/events" className="group">
                  <div className="flex flex-col items-center gap-3 p-5 bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border border-orange-200 h-full">
                    <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/25 group-hover:scale-110 transition-transform duration-300">
                      <Calendar className="w-7 h-7 text-white" />
                    </div>
                    <span className="text-sm font-semibold text-gray-700 text-center">
                      Eventos
                    </span>
                  </div>
                </Link>

                <Link to="/finance" className="group relative">
                  {stats.pendingCharges > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 z-10 min-w-[22px] h-[22px] px-1.5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center shadow-md ring-2 ring-white">
                      {stats.pendingCharges}
                    </span>
                  )}
                  <div className="flex flex-col items-center gap-3 p-5 bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-2xl hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border border-emerald-200 h-full">
                    <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/25 group-hover:scale-110 transition-transform duration-300">
                      <DollarSign className="w-7 h-7 text-white" />
                    </div>
                    <span className="text-sm font-semibold text-gray-700 text-center">
                      Finanzas
                    </span>
                  </div>
                </Link>

                <Link to="/members/requests" className="group">
                  <div className="flex flex-col items-center gap-3 p-5 bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border border-purple-200 h-full">
                    <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-700 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/25 group-hover:scale-110 transition-transform duration-300">
                      <User className="w-7 h-7 text-white" />
                    </div>
                    <span className="text-sm font-semibold text-gray-700 text-center">
                      Solicitudes
                    </span>
                  </div>
                </Link>

                <Link to="/reports" className="group">
                  <div className="flex flex-col items-center gap-3 p-5 bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-2xl hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border border-cyan-200 h-full">
                    <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-xl flex items-center justify-center shadow-lg shadow-cyan-500/25 group-hover:scale-110 transition-transform duration-300">
                      <FileText className="w-7 h-7 text-white" />
                    </div>
                    <span className="text-sm font-semibold text-gray-700 text-center">
                      Reportes
                    </span>
                  </div>
                </Link>

                <Link to="/settings" className="group">
                  <div className="flex flex-col items-center gap-3 p-5 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border border-gray-200 h-full">
                    <div className="w-14 h-14 bg-gradient-to-br from-gray-500 to-gray-600 rounded-xl flex items-center justify-center shadow-lg shadow-gray-500/25 group-hover:scale-110 transition-transform duration-300">
                      <Settings className="w-7 h-7 text-white" />
                    </div>
                    <span className="text-sm font-semibold text-gray-700 text-center">
                      Configuración
                    </span>
                  </div>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ============================================
// COMPONENTE PRINCIPAL
// ============================================
export default function DashboardPage() {
  // Data - usando scope del usuario
  const { profile } = useAuthStore();
  const { isClubDirector, isUnionAdmin, isAssociationAdmin, isMember } =
    usePermissions();
  const { data: clubs, isLoading: loadingClubs } = useAccessibleClubs();
  const { data: activePeriod } = useActivePeriod();
  /** Lo reporta WardsPendingConsents: cambia el mensaje del fallback sin rol. */
  const [tieneACargo, setTieneACargo] = useState(false);

  // Usar el club del director o el primero disponible
  const defaultClub =
    isClubDirector && profile?.directorOfClub
      ? profile.directorOfClub
      : clubs?.[0];

  if (loadingClubs) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Dashboard para miembros regulares
  if (isMember) {
    return <MemberDashboard />;
  }

  // Dashboard específico para Unión
  if (isUnionAdmin) {
    return <UnionDashboard />;
  }

  // Dashboard específico para Asociación
  if (isAssociationAdmin) {
    return <AssociationDashboard />;
  }

  if (isClubDirector) {
    return (
      <AdminDashboard
        role="club_director"
        defaultClub={defaultClub}
        activePeriod={activePeriod ?? undefined}
        clubs={clubs}
      />
    );
  }

  // Fallback - sin rol asignado.
  // Un ACUDIENTE cae aquí: no es miembro de ningún club, pero sus hijos sí
  // están inscritos a su cargo y él es quien debe subirles los documentos.
  // Antes este aviso amarillo era todo lo que veía y la tarjeta de menores
  // (más abajo en el árbol) nunca se renderizaba: el papá entraba y no tenía
  // forma de llegar a sus hijos. WardsPendingConsents se auto-oculta si no
  // tiene ninguno, así que para el resto no cambia nada.
  return (
    <div className="space-y-6">
      <WardsPendingConsents onLoaded={setTieneACargo} />
      {/* Al acudiente no se le alarma con "Sin rol": está donde debe estar. */}
      <Card
        className={
          tieneACargo
            ? "border-gray-200 bg-white shadow-sm"
            : "border-yellow-200 bg-yellow-50 shadow-lg"
        }
      >
        <CardContent className="py-8">
          <div className="text-center">
            <div
              className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg ${
                tieneACargo
                  ? "bg-gradient-to-br from-blue-100 to-blue-200"
                  : "bg-gradient-to-br from-yellow-200 to-yellow-300"
              }`}
            >
              <AlertCircle
                className={`w-8 h-8 ${tieneACargo ? "text-blue-600" : "text-yellow-600"}`}
              />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              {tieneACargo ? "Estás aquí como acudiente" : "Sin rol asignado"}
            </h2>
            <p className="text-gray-600 max-w-md mx-auto">
              {tieneACargo
                ? "Tu cuenta no pertenece a ningún club, pero sí puedes " +
                  "gestionar los documentos de las personas que tienes a cargo. " +
                  "Si además eres miembro de un club, pídele a tu director que " +
                  "te registre con este mismo correo."
                : "Tu cuenta aún no tiene un rol asignado en el sistema. Contacta a un administrador para que te asigne acceso."}
            </p>
            <Link
              to="/profile"
              className="inline-flex items-center gap-2 mt-4 text-blue-600 hover:text-blue-700 transition-colors duration-200"
            >
              <User className="w-4 h-4" />
              Ver mi perfil
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
